import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getActionableError } from "@/lib/errors";
import { isNumericScore, scoreToRating, scoreToTone, scoreValueToRating, sortScoreMetrics } from "@/lib/scoreGrade";
import { formatPlainTranscriptSpeakers, transcriptSpeakerPresentation } from "@/lib/transcriptSpeaker";
import { canonicalDemoSection, shouldUseReducedMobileStory } from "@/lib/demoRouting";
import Nav from "@/components/Nav";
import PublicDemoNav from "@/components/PublicDemoNav";
import LiveDemoFlowNav from "@/components/LiveDemoFlowNav";
import LiveDemoNextAction from "@/components/LiveDemoNextAction";
import BrandLockup from "@/components/BrandLockup";
import PublicThemeControl from "@/components/PublicThemeControl";
import ScoreInsight from "@/components/ScoreInsight";
import ConfidenceDisclosure from "@/components/ConfidenceDisclosure";
import FollowUpEmail from "@/components/FollowUpEmail";
import {
  DEMO_ANALYSIS,
  DEMO_CALL_BASE,
  DEMO_COACHING,
  DEMO_NOTE_SCORES,
  DEMO_NOTE_SCORES_BY_STYLE,
  DEMO_NOTE_VARIANTS,
  DEMO_RELATIONSHIP_DATA,
} from "@/data/liveDemoData";
import {
  SALES_DEMO_ANALYSIS,
  SALES_DEMO_CALL_BASE,
  SALES_DEMO_COACHING,
  SALES_DEMO_NOTE_SCORES,
  SALES_DEMO_NOTE_SCORES_BY_STYLE,
  SALES_DEMO_NOTE_VARIANTS,
  SALES_DEMO_RELATIONSHIP_DATA,
} from "@/data/salesLiveDemoData";
import { toast } from "sonner";
import { demoWritingDnaForVariant } from "@/data/demoWritingDnaProfiles";
import {
  Copy,
  Sparkle,
  ArrowClockwise,
  ClipboardText,
  ChatCircle,
  Brain,
  Target,
  MagnifyingGlass,
  ChartBar,
  Warning,
  Lightbulb,
  Flag,
  TrendUp,
  ClockCounterClockwise,
  WarningCircle,
  CheckCircle,
  Trash,
  X,
  UserCircle,
  Megaphone,
  Barbell,
  ArrowCircleUpRight,
  ChatsCircle,
  Handshake,
  CaretDown,
  ArrowLeft,
  ArrowRight,
  PhoneCall,
  Users,
} from "@phosphor-icons/react";


const api = new Proxy({}, {
  get() {
    return async () => {
      throw new Error("The public live demo is local-only and cannot call the backend.");
    };
  },
});

const trackProductEvent = () => {};

// The paid/public mobile demo is intentionally a guided, swipeable story.
// It proves the core value quickly while keeping Relationship directly explorable;
// the primary controls still preserve the suggested progression.
const USE_REDUCED_MOBILE_STORY = true;

const STYLES = [
  { key: "first_person", label: "First-Person" },
  { key: "salesforce", label: "Salesforce" },
  { key: "hubspot", label: "HubSpot" },
  { key: "close", label: "Close" },
  { key: "minimal", label: "Minimal" },
  { key: "executive", label: "Executive" },
  { key: "detailed_history", label: "Detailed History" },
];

const LIVE_DEMO_DATA = {
  renewals: {
    callBase: DEMO_CALL_BASE,
    analysis: DEMO_ANALYSIS,
    coaching: DEMO_COACHING,
    noteScores: DEMO_NOTE_SCORES,
    noteScoresByStyle: DEMO_NOTE_SCORES_BY_STYLE,
    noteVariants: DEMO_NOTE_VARIANTS,
    relationship: DEMO_RELATIONSHIP_DATA,
  },
  sales: {
    callBase: SALES_DEMO_CALL_BASE,
    analysis: SALES_DEMO_ANALYSIS,
    coaching: SALES_DEMO_COACHING,
    noteScores: SALES_DEMO_NOTE_SCORES,
    noteScoresByStyle: SALES_DEMO_NOTE_SCORES_BY_STYLE,
    noteVariants: SALES_DEMO_NOTE_VARIANTS,
    relationship: SALES_DEMO_RELATIONSHIP_DATA,
  },
};

function useMobileDemoViewport() {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false
  ));

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const query = window.matchMedia("(max-width: 767px)");
    const update = (event) => setIsMobile(event.matches);
    setIsMobile(query.matches);
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  return isMobile;
}

function fmt(t) {
  if (t == null) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  const datePart = date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
}

// Normalize a confidence value into 'high' | 'medium' | 'low'
function confLevel(c) {
  if (c == null) return null;
  if (typeof c === "number") {
    if (c >= 75) return "high";
    if (c >= 50) return "medium";
    return "low";
  }
  const s = String(c).toLowerCase();
  if (s === "high" || s === "medium" || s === "low") return s;
  return null;
}

function ConfChip({ confidence, testId, inline = false }) {
  const lvl = confLevel(confidence);
  if (lvl !== "medium") return null;
  return <ConfidenceDisclosure testId={testId} inline={inline} />;
}

// Extract text + confidence from either a plain string or a {text, confidence} object
function itemParts(it) {
  if (it == null) return { text: "", confidence: null };
  if (typeof it === "string") {
    return { text: localizeSellerRole(it), confidence: null };
  }
  if (typeof it === "object") {
    return {
      text: localizeSellerRole(it.text || it.description || JSON.stringify(it)),
      confidence: it.confidence,
    };
  }
  return { text: localizeSellerRole(String(it)), confidence: null };
}

function localizeSellerRole(value) {
  if (typeof value !== "string") return value;

  const normalized = value
    .replace(/\bDer Commercial\b/g, "Der Vertriebsmitarbeiter")
    .replace(/\bder Commercial\b/g, "der Vertriebsmitarbeiter")
    .replace(/\bDen Commercial\b/g, "Den Vertriebsmitarbeiter")
    .replace(/\bden Commercial\b/g, "den Vertriebsmitarbeiter")
    .replace(/\bDem Commercial\b/g, "Dem Vertriebsmitarbeiter")
    .replace(/\bdem Commercial\b/g, "dem Vertriebsmitarbeiter")
    .replace(/\bEin Commercial\b/g, "Ein Vertriebsmitarbeiter")
    .replace(/\bein Commercial\b/g, "ein Vertriebsmitarbeiter")
    .replace(/\bEinen Commercial\b/g, "Einen Vertriebsmitarbeiter")
    .replace(/\beinen Commercial\b/g, "einen Vertriebsmitarbeiter");

  const englishNormalized = normalized
    .replace(/\b(the|a|an)\s+commercial\b/gi, (_, article) => `${article} rep`)
    .replace(/\bcommercial(?=\s+(?:must|should|will|needs|committed|said|asked|offered|agreed|clarified|followed|handled|missed|could|would)\b)/gi, "rep");
  if (!/(?:sales[ _-]?rep|sales representative|account manager|salesperson|sales agent)/i.test(englishNormalized)) return englishNormalized;
  const lower = englishNormalized.toLocaleLowerCase();
  let role = "rep";
  if (/\b(?:il|lo|la|cliente|impegno|impegnat|verificare|aggiornare|possibilit[aà]|pomeriggio)\b/i.test(lower)) {
    role = "commerciale";
  } else if (/\b(?:le|la|client|engagement|vérifier|mettre à jour|commerciale?)\b/i.test(lower)) {
    role = "commercial";
  } else if (/\b(?:el|la|cliente|compromiso|verificar|actualizar|comercial)\b/i.test(lower)) {
    role = "representante comercial";
  } else if (/\b(?:der|die|kunde|zusage|prüfen|aktualisieren|vertrieb)\b/i.test(lower)) {
    role = "Vertriebsmitarbeiter";
  }
  return englishNormalized.replace(/(?:sales[ _-]?rep|sales representative|account manager|salesperson|sales agent)/gi, role);
}

function longitudinalItemText(item) {
  if (item == null) return "";
  if (typeof item === "string") return localizeSellerRole(item);
  if (typeof item !== "object") return String(item);
  return localizeSellerRole(item.what || item.text || item.description || item.answer || "");
}

function comparisonTokens(value) {
  const stopWords = new Set([
    "alla", "allo", "anche", "come", "dalla", "delle", "della", "degli", "dello", "dopo",
    "into", "from", "that", "the", "this", "with", "pour", "dans", "avec", "para", "con",
  ]);
  return new Set(
    longitudinalItemText(value)
      .toLocaleLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .match(/[a-z0-9]+/g)
      ?.filter((token) => token.length >= 4 && !stopWords.has(token)) || []
  );
}

function substantiallyOverlaps(left, right) {
  const a = comparisonTokens(left);
  const b = comparisonTokens(right);
  if (!a.size || !b.size) return false;
  let shared = 0;
  a.forEach((token) => {
    if (b.has(token)) shared += 1;
  });
  return shared / Math.min(a.size, b.size) >= 0.6;
}

function redundantHeadline(item, objections, themes, promises) {
  const text = longitudinalItemText(item).toLocaleLowerCase();
  const promiseCue = /(?:impegn|promess|commit|sales[ _-]?rep|commerciale|commercial|zusage)/i;
  const objectionCue = /(?:obiezion|objection|objeción|einwand)/i;
  const themeCue = /(?:\btema\b|\btheme\b|\bthème\b|\bthema\b)/i;
  if (promises.length && promiseCue.test(text)) return true;
  if (objections.length && objectionCue.test(text)) return true;
  if (themes.length && themeCue.test(text)) return true;
  return [...objections, ...themes, ...promises].some((detail) => substantiallyOverlaps(item, detail));
}

function signalValue(value) {
  if (value == null || value === "") return "Not established";
  if (typeof value === "object") {
    if (value.text != null && value.text !== "") return String(value.text);
    if (value.score != null) return scoreToRating(value.score, { fallback: "Not established" });
    return scoreValueToRating(value.value);
  }
  return scoreValueToRating(value);
}

function signalScoreToneClass(value, muted = false) {
  let score = value;
  if (value && typeof value === "object") {
    if (value.text != null && value.text !== "") return muted ? "text-slate-500" : "";
    score = value.score ?? value.value;
  }
  if (!isNumericScore(score)) return muted ? "text-slate-500" : "";
  return `score-text score-text--${scoreToTone(score)}`;
}

function DeltaList({ title, items, tone = "violet" }) {
  const visible = Array.isArray(items) ? items.filter(longitudinalItemText).slice(0, 4) : [];
  if (!visible.length) return null;
  const colors = {
    violet: "text-violet-300",
    amber: "text-amber-300",
    emerald: "text-emerald-300",
    red: "text-red-300",
  };
  return (
    <div className="longitudinal-list">
      <div className={`longitudinal-list__title ${colors[tone] || colors.violet}`}>
        {title}
      </div>
      <ul className="longitudinal-list__items">
        {visible.map((item, index) => (
          <li key={index} className="flex gap-2">
            <span className={colors[tone] || colors.violet}>•</span>
            <span>{longitudinalItemText(item)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function readLongitudinalDeltaExpanded(storageKey) {
  if (!storageKey || typeof window === "undefined") return true;
  try {
    return window.sessionStorage.getItem(storageKey) !== "collapsed";
  } catch {
    return true;
  }
}

function LongitudinalDelta({
  update,
  relationshipPath,
  relationshipName,
  relationshipReturnState,
  persistenceKey,
}) {
  const storageKey = persistenceKey
    ? `callsect:longitudinal-delta:${persistenceKey}`
    : null;
  const [expanded, setExpanded] = useState(() => readLongitudinalDeltaExpanded(storageKey));

  useEffect(() => {
    setExpanded(readLongitudinalDeltaExpanded(storageKey));
  }, [storageKey]);

  const toggleExpanded = () => {
    setExpanded((current) => {
      const next = !current;
      if (storageKey && typeof window !== "undefined") {
        try {
          window.sessionStorage.setItem(storageKey, next ? "expanded" : "collapsed");
        } catch {
          // Keep the interaction working even when browser storage is unavailable.
        }
      }
      return next;
    });
  };

  if (!update) return null;

  const changes = update.changes || {};
  const isComparison = Boolean(update.previous_call_id);
  const signalChanges = changes.signal_changes || {};
  const signalDefinitions = [
    ["Stance", signalChanges.attitude],
    ["Buying intent", signalChanges.buying_intent],
    ["Trust", signalChanges.trust],
    ["Tone", signalChanges.emotional_tone],
  ];
  const visibleSignals = isComparison
    ? signalDefinitions.filter(([, signal]) => signal && (signal.from != null || signal.to != null))
    : [];
  const friction = changes.friction || {};
  const commercial = friction.commercial_objections || changes.objections || {};
  const blockers = friction.operational_blockers || {};
  const preferences = friction.process_preferences || {};
  const commitments = changes.commitments || {};
  const legacyPromises = changes.promises || {};
  const themes = changes.themes || changes.recurring_themes || {};

  const candidates = [
    ...(Array.isArray(changes.summary) ? changes.summary : []),
    ...(commercial.new || []),
    ...(commercial.recurring || []),
    ...(blockers.new || []),
    ...(blockers.recurring || []),
    ...(preferences.new || []),
    ...(commitments.new || legacyPromises.made || []),
    ...(commitments.completed || legacyPromises.fulfilled || []),
    ...(themes.new || []),
    ...(themes.recurring || []),
  ];

  const visibleItems = candidates
    .filter(longitudinalItemText)
    .filter((item, index, all) => (
      !all.slice(0, index).some((previous) => substantiallyOverlaps(item, previous))
    ))
    .slice(0, 4);

  if (!visibleItems.length) return null;

  const title = isComparison ? "Relationship update" : "Initial relationship context";

  return (
    <section
      className={`longitudinal-delta longitudinal-delta--compact${expanded ? " is-expanded" : " is-collapsed"}`}
      data-testid="longitudinal-call-delta"
    >
      <div className="longitudinal-delta__header">
        <button
          type="button"
          className="longitudinal-delta__toggle"
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-controls="relationship-context-details"
        >
          <span className="longitudinal-delta__title">
            <ClockCounterClockwise size={17} weight="duotone" />
            {title}
          </span>
        </button>

        <div className="longitudinal-delta__header-actions">
          {relationshipPath && (
            <Link
              to={relationshipPath}
              state={relationshipReturnState}
              className="longitudinal-delta__relationship-link"
              aria-label={`Open live relationship context for ${relationshipName || "this customer"}`}
            >
              <span>Live relationship context</span>
              <ArrowRight size={14} weight="bold" aria-hidden="true" />
            </Link>
          )}
          <button
            type="button"
            className="longitudinal-delta__expand-button"
            onClick={toggleExpanded}
            aria-expanded={expanded}
            aria-controls="relationship-context-details"
            aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
          >
            <CaretDown
              size={17}
              weight="bold"
              className="longitudinal-delta__chevron"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {expanded && (
        <div id="relationship-context-details" className="longitudinal-delta__content">
          <ul className="longitudinal-delta__summary longitudinal-delta__summary--compact">
            {visibleItems.map((item, index) => (
              <li key={index}>
                <TrendUp size={15} aria-hidden="true" />
                <span>{longitudinalItemText(item)}</span>
              </li>
            ))}
          </ul>

          {visibleSignals.length > 0 && (
            <div className="longitudinal-signal-grid" aria-label="Updated relationship signals">
              {visibleSignals.map(([label, signal]) => (
                <div key={label} className="longitudinal-signal">
                  <div className="longitudinal-signal__label">{label}</div>
                  <div className="longitudinal-signal__value">
                    <span className={signalScoreToneClass(signal.from, true)}>{signalValue(signal.from)}</span>
                    <span className="mx-2 text-violet-400">→</span>
                    <span className={signalScoreToneClass(signal.to)}>{signalValue(signal.to)}</span>
                  </div>
                  {signal.direction && signal.direction !== "unknown" && (
                    <div className="longitudinal-signal__direction">{signal.direction}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const SCORE_LABELS = {
  human_writing: "Human writing",
  template_language: "Template language",
  business_reasoning: "Business reasoning",
  negotiation_insight: "Negotiation insight",
  emotional_accuracy: "Emotional accuracy",
  customer_psychology: "Customer psychology",
  future_usefulness: "Future usefulness",
  writing_dna_similarity: "Writing DNA match",
};

const SCORE_DESCRIPTIONS = {
  human_writing: "How natural and human the note sounds, without formulaic or AI-like phrasing.",
  template_language: "How well the note avoids generic, repetitive, or canned CRM language.",
  business_reasoning: "How clearly the note explains the commercial logic, decisions, and cause-and-effect behind the conversation.",
  negotiation_insight: "How well the note captures objections, leverage, trade-offs, and decision dynamics.",
  emotional_accuracy: "How faithfully the note reflects tone, reactions, tension, rapport, and conversational subtext.",
  customer_psychology: "How clearly the note captures the customer's motivations, concerns, intent, and behavior.",
  future_usefulness: "How useful the note will be when preparing the next contact and deciding what to do next.",
  writing_dna_similarity: "How closely the note matches the user's learned writing style and reasoning patterns.",
};

function scoreTextClass(value, invert = false) {
  return `score-grade score-text score-text--${scoreTone(value, invert)}`;
}

function QualityScorePanel({ scores }) {
  if (!scores) return null;
  const cats = Object.keys(SCORE_LABELS);
  const overall = scores.overall || 0;
  const dnaProfile = scores.dna_profile || {};
  const dnaAlignment = scores.dna_alignment || {};
  const weakCategories = cats.filter((key) => Number.isFinite(scores[key]?.score) && scores[key].score < 70);

  return (
    <section className="note-quality" data-testid="quality-scores">
      <div className="note-quality__header">
        <div>
          <div className="note-quality__title">Note quality</div>
          <p>Scores always refer to the CRM note version currently shown above.</p>
          {dnaAlignment.applied && (
            <span className="note-quality__alignment">Demo sample Writing DNA · alignment applied</span>
          )}
          {dnaProfile.sample_profile && dnaProfile.sample_fit && (
            <p data-testid="demo-writing-dna-fit">{dnaProfile.sample_fit}</p>
          )}
        </div>
        <div className="note-quality__overall">
          <span className={scoreTextClass(overall)} data-testid="overall-score">{scoreToRating(overall)}</span>
          <small>overall</small>
        </div>
      </div>

      <div className="note-quality__grid">
        {cats.map((key, index) => {
          const value = scores[key] || {};
          const isDna = key === "writing_dna_similarity";
          const calibrated = isDna
            ? Boolean(dnaProfile.calibrated && Number.isFinite(value.score))
            : Number.isFinite(value.score);
          const score = calibrated ? Math.max(0, Math.min(100, value.score)) : 0;
          return (
            <div
              key={key}
              className={`note-quality__metric ${calibrated ? `note-quality__metric--${scoreTone(score)}` : "note-quality__metric--uncalibrated"}`}
              data-testid={`score-${key}`}
              style={{ animationDelay: `${index * 45}ms` }}
            >
              <div className="note-quality__metric-head">
                <div className="note-quality__metric-label">
                  <span>{SCORE_LABELS[key]}</span>
                  <ScoreInsight label={SCORE_LABELS[key]} description={SCORE_DESCRIPTIONS[key]} />
                </div>
              </div>
              <div className={`score-meter${calibrated ? "" : " is-uncalibrated"}`}>
                <div
                  className={calibrated ? `score-meter__fill score-meter__fill--${scoreTone(score)}` : "score-meter__fill"}
                  style={{ "--score-meter-scale": (calibrated ? score : 0) / 100 }}
                />
              </div>
              {isDna && (
                <div className="note-quality__metric-meta">
                  {dnaProfile.calibrated ? (
                    <>
                      <span>{dnaProfile.maturity_label || "Calibrated profile"}</span>
                      <span>{dnaProfile.confidence}% demo sample profile confidence</span>
                    </>
                  ) : (
                    <span>Not calibrated · add CRM examples in Writing DNA</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {weakCategories.length > 0 && (
        <div className="note-quality__weak">
          <div className="note-quality__weak-title">Areas to strengthen</div>
          {weakCategories.map((key) => {
            const value = scores[key];
            return (
              <article key={key} data-testid={`weak-${key}`} className={`note-quality__weak-card note-quality__weak-card--${scoreTone(value.score)}`}>
                <div className="note-quality__weak-head">
                  <span>{SCORE_LABELS[key]}</span>
                </div>
                {value.reason && (
                  <p><span>Reason</span>{value.reason}</p>
                )}
                {value.suggestion && (
                  <p><span className="text-emerald-400">Suggested improvement</span>{value.suggestion}</p>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function RevealLinesMark({ compact = false }) {
  return (
    <span className={`reveal-lines-mark${compact ? " reveal-lines-mark--compact" : ""}`} aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}

function demoCallForStage(stage = 0, demoData = LIVE_DEMO_DATA.renewals) {
  const initialNoteScores = demoWritingDnaForVariant(
    demoData.noteScoresByStyle,
    demoData.noteScores,
    "first_person",
    "default"
  );

  return {
    ...demoData.callBase,
    analysis: stage >= 1 ? demoData.analysis : null,
    notes: stage >= 2
      ? {
          first_person: {
            content: demoData.noteVariants.first_person.default,
            scores: initialNoteScores,
            language_mode: "english",
            dna_alignment: initialNoteScores?.dna_alignment || null,
          },
        }
      : {},
    coaching: stage >= 3 ? demoData.coaching : null,
  };
}

const DEMO_FLOW_STEPS = ["transcript", "analysis", "coaching", "notes", "relationship"];

function readDemoUnlockedIndex(storageKey) {
  if (typeof window === "undefined") return 0;
  const stored = Number(window.sessionStorage.getItem(storageKey));
  if (!Number.isFinite(stored)) return 0;
  return Math.max(0, Math.min(DEMO_FLOW_STEPS.length - 1, stored));
}

function readDemoStage() {
  // Keep the synthetic outputs ready in memory; navigation disclosure is
  // controlled separately by the guided-path unlock state.
  return 3;
}

export default function LiveDemoCallPage({ demoUseCase = "renewals" }) {
  const demoData = LIVE_DEMO_DATA[demoUseCase] || LIVE_DEMO_DATA.renewals;
  const demoBasePath = `/demo/${demoUseCase === "sales" ? "sales" : "renewals"}`;
  const demoRelationshipPath = `${demoBasePath}/relationship`;
  const demoMode = true;
  const isMobileDemo = useMobileDemoViewport();
  const demoUnlockStorageKey = `callsect:live-demo-unlocked:${demoUseCase}`;
  const { id: routeId } = useParams();
  const id = demoMode ? demoData.callBase.id : routeId;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDemoStage = demoMode ? readDemoStage() : 0;
  const [demoStage, setDemoStage] = useState(initialDemoStage);
  const [demoUnlockedIndex, setDemoUnlockedIndex] = useState(() => readDemoUnlockedIndex(demoUnlockStorageKey));
  const [demoCoachingLoading, setDemoCoachingLoading] = useState(false);
  const [demoFlowBusy, setDemoFlowBusy] = useState(null);
  const [call, setCall] = useState(() => demoMode ? demoCallForStage(initialDemoStage, demoData) : null);
  const [loading, setLoading] = useState(!demoMode);
  const [relationshipContext, setRelationshipContext] = useState(null);

  useEffect(() => {
    if (!demoMode) return;
    const stage = readDemoStage();
    setDemoStage(stage);
    setCall(demoCallForStage(stage, demoData));
    setDemoUnlockedIndex(readDemoUnlockedIndex(demoUnlockStorageKey));
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("callsect:live-demo-use-case", demoUseCase);
    }
  }, [demoData, demoMode, demoUnlockStorageKey, demoUseCase]);

  const requestedTab = searchParams.get("section");
  const deepDiveTarget = searchParams.get("deepDiveTarget") || location.state?.deepDiveTarget || null;
  const transcriptAt = searchParams.get("transcriptAt");
  const firstResult = demoMode ? false : Boolean(location.state?.firstResult);
  const requestedActiveTab = canonicalDemoSection(requestedTab);
  const requestedDemoIndex = DEMO_FLOW_STEPS.indexOf(requestedActiveTab);
  const activeTab = demoMode && !isMobileDemo && requestedDemoIndex > demoUnlockedIndex
    ? DEMO_FLOW_STEPS[demoUnlockedIndex]
    : requestedActiveTab;

  useEffect(() => {
    if (!demoMode || requestedTab == null || requestedTab === activeTab) return;
    const canonical = new URLSearchParams(searchParams);
    canonical.set("section", activeTab);
    setSearchParams(canonical, { replace: true, preventScrollReset: true });
  }, [activeTab, demoMode, requestedTab, searchParams, setSearchParams]);

  useLayoutEffect(() => {
    if (!demoMode || typeof window === "undefined") return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [demoMode, location.pathname]);

  // Keep the navigation request and its DOM anchors available before any
  // callbacks/effects read them. The previous ordering referenced
  // sectionNavigationRequest before its useState declaration and crashed the
  // Live Demo during render on both mobile and desktop.
  const sectionNavigationSequenceRef = useRef(0);
  const [sectionNavigationRequest, setSectionNavigationRequest] = useState(null);
  const sectionAnchorRef = useRef(null);
  const sectionContentRef = useRef(null);
  const desktopSectionScrollRef = useRef(null);

  const persistDemoStage = useCallback((nextStage) => {
    const normalized = Math.max(0, Math.min(3, Number(nextStage) || 0));
    setDemoStage(normalized);
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("callsect:live-demo-stage", String(normalized));
    }
  }, []);

  const persistDemoUnlockedIndex = useCallback((nextIndex) => {
    const normalized = Math.max(0, Math.min(DEMO_FLOW_STEPS.length - 1, Number(nextIndex) || 0));
    setDemoUnlockedIndex((current) => Math.max(current, normalized));
    if (typeof window !== "undefined") {
      const current = readDemoUnlockedIndex(demoUnlockStorageKey);
      window.sessionStorage.setItem(demoUnlockStorageKey, String(Math.max(current, normalized)));
    }
  }, [demoUnlockStorageKey]);

  const revealDemoCoaching = useCallback(async (options = {}) => {
    const force = options?.force === true;
    if (!demoMode || (!force && demoStage < 2) || demoCoachingLoading) return;
    if (call?.coaching) {
      persistDemoStage(3);
      return;
    }

    setDemoCoachingLoading(true);
    await new Promise((resolve) => window.setTimeout(resolve, 3600));
    setCall((current) => ({ ...current, coaching: demoData.coaching }));
    persistDemoStage(3);
    setDemoCoachingLoading(false);
  }, [call?.coaching, demoCoachingLoading, demoData.coaching, demoMode, demoStage, persistDemoStage]);

  const updateTabParam = useCallback((key) => {
    const next = new URLSearchParams(searchParams);
    if (key === "analysis" && !demoMode) next.delete("section");
    else next.set("section", key);
    if (key !== "transcript") next.delete("transcriptAt");
    setSearchParams(next, { replace: !demoMode, preventScrollReset: true });
  }, [demoMode, searchParams, setSearchParams]);

  const transcriptHrefFor = useCallback((timestamp) => {
    const value = String(timestamp || "").trim();
    const next = new URLSearchParams(searchParams);
    next.set("section", "transcript");
    if (value) next.set("transcriptAt", value);
    else next.delete("transcriptAt");
    next.delete("deepDiveTarget");
    const query = next.toString();
    return `${location.pathname}${query ? `?${query}` : ""}`;
  }, [location.pathname, searchParams]);

  const setActiveTab = useCallback((key) => {
    if (demoMode) {
      updateTabParam(key);
      return;
    }

    trackProductEvent("result_section_opened", {
      section: key,
      call_count: Number.isFinite(location.state?.callCount) ? location.state.callCount : undefined,
      outcome: firstResult ? "first_result" : "existing_result",
    });
    updateTabParam(key);
  }, [demoMode, firstResult, location.state?.callCount, updateTabParam]);

  const scrollToCallSection = useCallback((behavior = "smooth") => {
    if (typeof window === "undefined") return false;
    const target = sectionContentRef.current;
    const tabs = sectionAnchorRef.current;
    if (!target) return false;

    let offset = 16;
    const mobileBar = document.querySelector(".app-mobile-bar");
    if (mobileBar && window.getComputedStyle(mobileBar).display !== "none") {
      offset += mobileBar.getBoundingClientRect().height;
    }

    if (tabs) {
      const tabsStyle = window.getComputedStyle(tabs);
      if (tabsStyle.position === "sticky" || tabsStyle.position === "fixed") {
        offset += (Number.parseFloat(tabsStyle.top) || 0)
          + tabs.getBoundingClientRect().height
          + 8;
      }
    }

    const targetTop = window.scrollY + target.getBoundingClientRect().top - offset;
    window.scrollTo({ top: Math.max(0, targetTop), behavior });
    return true;
  }, []);

  const openDemoSection = useCallback((key) => {
    // The public demo is a guided sequence: every step starts at the top of
    // the page. Do not inherit the real app's in-place scroll behavior.
    if (demoMode) {
      sectionNavigationSequenceRef.current += 1;
      setSectionNavigationRequest({
        key,
        sequence: sectionNavigationSequenceRef.current,
      });
      setActiveTab(key);
      return;
    }

    const isMobile = typeof window !== "undefined"
      && window.matchMedia("(max-width: 760px)").matches;

    // Match the real product: desktop switches panels in place, while mobile
    // still guides the user directly to the selected section.
    if (!isMobile) {
      setSectionNavigationRequest(null);
      desktopSectionScrollRef.current = typeof window !== "undefined"
        ? { top: window.scrollY, left: window.scrollX }
        : null;
      setActiveTab(key);
      return;
    }

    sectionNavigationSequenceRef.current += 1;
    setSectionNavigationRequest({
      key,
      sequence: sectionNavigationSequenceRef.current,
    });
    setActiveTab(key);
  }, [demoMode, setActiveTab]);

  useLayoutEffect(() => {
    const request = sectionNavigationRequest;
    if (!request || activeTab !== request.key) return undefined;

    let secondFrame;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        if (demoMode) {
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
          return;
        }
        scrollToCallSection("smooth");
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [activeTab, demoMode, scrollToCallSection, sectionNavigationRequest]);

  useLayoutEffect(() => {
    const position = desktopSectionScrollRef.current;
    if (!position || typeof window === "undefined") return undefined;

    desktopSectionScrollRef.current = null;
    window.scrollTo({ top: position.top, left: position.left, behavior: "auto" });
    return undefined;
  }, [activeTab]);

  const [activeStyle, setActiveStyle] = useState("first_person");
  const [crmNoteLanguage, setCrmNoteLanguage] = useState(
    () => localStorage.getItem("callsect_crm_note_language") || "english"
  );
  const [generating, setGenerating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [findingsStep, setFindingsStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [transcriptTargetIndex, setTranscriptTargetIndex] = useState(null);
  const [highlightedTranscriptIndex, setHighlightedTranscriptIndex] = useState(null);
  const audioRef = useRef();
  const deepDiveTargetHandledRef = useRef(null);
  const firstResultTrackedRef = useRef(false);
  const [showFirstResultGuide, setShowFirstResultGuide] = useState(
    () => localStorage.getItem("callsect:first-result-guide-dismissed") !== "1"
  );

  const load = async () => {
    if (demoMode) {
      setCall(demoCallForStage(readDemoStage(), demoData));
      setLoading(false);
      return;
    }
    const { data } = await api.get(`/calls/${id}`);
    setCall(data);
    setLoading(false);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
    if (demoMode) {
      setRelationshipContext(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setRelationshipContext(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode, id]);

  useEffect(() => {
    if (demoMode || !call?.id || !firstResult || firstResultTrackedRef.current) return;
    firstResultTrackedRef.current = true;
    trackProductEvent("first_result_viewed", {
      call_count: Number.isFinite(location.state?.callCount) ? location.state.callCount : 1,
      section: call.analysis ? "analysis_ready" : "transcript_only",
    });
  }, [call?.analysis, call?.id, demoMode, firstResult, location.state?.callCount]);

  useEffect(() => {
    if (demoMode) {
      setRelationshipContext(null);
      return undefined;
    }
    let cancelled = false;

    const normalize = (value) => String(value || "").trim().toLocaleLowerCase();

    const loadRelationshipContext = async () => {
      if (!call?.id || (!call.customer_name && !call.relationship_id)) {
        if (!cancelled) setRelationshipContext(null);
        return;
      }

      try {
        const { data: customers } = await api.get("/customers");
        const directKey = call.relationship_id || call.relationship_key;
        const normalizedName = normalize(call.customer_name);
        const normalizedCompany = normalize(call.company);

        const matchedCustomer = (customers || []).find((customer) => (
          directKey
          && [customer.key, customer.id].some((value) => String(value || "") === String(directKey))
        )) || (customers || []).find((customer) => (
          normalize(customer.name) === normalizedName
          && (!normalizedCompany || !customer.company || normalize(customer.company) === normalizedCompany)
        )) || (directKey ? {
          key: directKey,
          id: directKey,
          name: call.customer_name,
          company: call.company,
        } : null);

        const relationshipKey = matchedCustomer?.key || matchedCustomer?.id;
        if (!relationshipKey) {
          if (!cancelled) setRelationshipContext(null);
          return;
        }

        const { data: timeline } = await api.get(
          `/customers/${encodeURIComponent(relationshipKey)}/timeline`
        );
        const chronologicalCalls = [...(timeline?.milestones || [])].sort(
          (left, right) => new Date(left.date).getTime() - new Date(right.date).getTime()
        );
        const currentIndex = chronologicalCalls.findIndex(
          (milestone) => String(milestone.call_id) === String(call.id)
        );

        if (!cancelled) {
          setRelationshipContext({
            key: relationshipKey,
            name: timeline?.customer?.name || matchedCustomer?.name || call.customer_name,
            company: timeline?.customer?.company || matchedCustomer?.company || call.company,
            previousCall: currentIndex > 0 ? chronologicalCalls[currentIndex - 1] : null,
            nextCall: currentIndex >= 0 && currentIndex < chronologicalCalls.length - 1
              ? chronologicalCalls[currentIndex + 1]
              : null,
          });
        }
      } catch {
        if (!cancelled) setRelationshipContext(null);
      }
    };

    loadRelationshipContext();
    return () => {
      cancelled = true;
    };
  }, [call?.company, call?.customer_name, call?.id, call?.relationship_id, call?.relationship_key, demoMode]);

  useEffect(() => {
    localStorage.setItem("callsect_crm_note_language", crmNoteLanguage);
  }, [crmNoteLanguage]);

  useEffect(() => {
    if (!transcriptAt) return;
    const seconds = parseTimestampSeconds(transcriptAt);
    const segments = Array.isArray(call?.segments) ? call.segments : [];
    if (!segments.length || !Number.isFinite(seconds)) return;

    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    segments.forEach((segment, index) => {
      const start = Number(segment?.start);
      if (!Number.isFinite(start)) return;
      const distance = Math.abs(start - seconds);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    setTranscriptTargetIndex(nearestIndex);
  }, [call?.segments, transcriptAt]);

  useEffect(() => {
    if (activeTab !== "transcript" || transcriptTargetIndex == null) return undefined;

    const frame = window.requestAnimationFrame(() => {
      const target = document.querySelector(
        `[data-transcript-index="${transcriptTargetIndex}"]`
      );
      if (!target) return;

      target.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedTranscriptIndex(transcriptTargetIndex);
    });

    const clearHighlight = window.setTimeout(() => {
      setHighlightedTranscriptIndex(null);
      setTranscriptTargetIndex(null);
    }, 2600);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(clearHighlight);
    };
  }, [activeTab, transcriptTargetIndex]);

  useEffect(() => {
    const targetKey = deepDiveTarget;
    if (!targetKey || !call?.analysis) return undefined;

    const token = `${id}:${targetKey}`;
    if (deepDiveTargetHandledRef.current === token) return undefined;

    if (activeTab !== "analysis") {
      setActiveTab("analysis");
      return undefined;
    }

    let clearTarget = null;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(`deep-dive-${targetKey}`)
        || document.getElementById("deep-dive");
      if (!target) return;

      target.scrollIntoView({ behavior: "smooth", block: "center" });
      target.classList.add("is-linked-target");
      deepDiveTargetHandledRef.current = token;
      clearTarget = window.setTimeout(() => {
        target.classList.remove("is-linked-target");
      }, 2600);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      if (clearTarget) window.clearTimeout(clearTarget);
    };
  }, [activeTab, call?.analysis, deepDiveTarget, id, setActiveTab]);

  const runAnalysis = async (options = {}) => {
    const navigateToAnalysis = options?.navigateToAnalysis !== false;
    setAnalyzing(true);
    setFindingsStep(1);
    if (demoMode && navigateToAnalysis) openDemoSection("analysis");
    const findingsTimer = setTimeout(() => {
      setFindingsStep(2);
    }, demoMode ? 850 : 1500);
    try {
      if (demoMode) {
        await new Promise((resolve) => setTimeout(resolve, 2200));
        setCall((current) => ({
          ...current,
          analysis: demoData.analysis,
        }));
        persistDemoStage(1);
        toast.success("Synthetic dissection ready");
      } else {
        const { data } = await api.post(`/calls/${id}/analyze`);
        setCall((c) => ({
          ...c,
          analysis: data.analysis,
          coaching: data.coaching,
          relationship_update: data.relationship_update || c.relationship_update,
        }));
        toast.success("Analysis complete");
      }
    } catch (error) {
      toast.error(getActionableError(error, "The dissection could not be completed. Try again."));
    } finally {
      clearTimeout(findingsTimer);
      setAnalyzing(false);
      setFindingsStep(0);
    }
  };

  const generate = async (style) => {
    setGenerating(true);
    setActiveStyle(style);
    try {
      if (demoMode) {
        await new Promise((resolve) => setTimeout(resolve, 1350));
        const variants = demoData.noteVariants[style] || demoData.noteVariants.first_person;
        const demoScores = demoWritingDnaForVariant(demoData.noteScoresByStyle, demoData.noteScores, style, "default");
        setCall((current) => ({
          ...current,
          notes: {
            ...(current.notes || {}),
            [style]: {
              content: variants.default,
              scores: demoScores,
              language_mode: "english",
              dna_alignment: demoScores?.dna_alignment || null,
            },
          },
        }));
        persistDemoStage(2);
        toast.success("Synthetic CRM note ready");
      } else {
        const { data } = await api.post(`/calls/${id}/notes/generate`, {
          style,
          use_writing_profile: true,
          output_language: crmNoteLanguage,
        });
        setCall((c) => ({
          ...c,
          notes: {
            ...(c.notes || {}),
            [style]: {
              content: data.content,
              scores: data.scores,
              language_mode: data.language_mode,
              dna_alignment: data.dna_alignment || null,
            },
          },
        }));
        toast.success(`${style.replace(/_/g, " ")} note ready`);
        trackProductEvent("crm_note_generated", {
          section: style,
          call_count: Number.isFinite(location.state?.callCount) ? location.state.callCount : undefined,
          outcome: crmNoteLanguage,
        });
      }
    } catch (error) {
      toast.error(getActionableError(error, "The CRM note could not be generated. Try again."));
    } finally {
      setGenerating(false);
    }
  };

  const rewrite = async (instruction) => {
    const current = call?.notes?.[activeStyle]?.content;
    if (!current) return;
    setGenerating(true);
    try {
      if (demoMode) {
        await new Promise((resolve) => setTimeout(resolve, 700));
        const variants = demoData.noteVariants[activeStyle] || demoData.noteVariants.first_person;
        const rewritten = variants[instruction] || variants.default;
        const demoScores = demoWritingDnaForVariant(demoData.noteScoresByStyle, demoData.noteScores, activeStyle, instruction);
        setCall((existing) => ({
          ...existing,
          notes: {
            ...(existing.notes || {}),
            [activeStyle]: {
              ...(existing.notes?.[activeStyle] || {}),
              content: rewritten,
              scores: demoScores,
              dna_alignment: demoScores?.dna_alignment || null,
            },
          },
        }));
        toast.success("Synthetic note rewritten");
      } else {
        const { data } = await api.post(`/calls/${id}/notes/rewrite`, {
          text: current,
          instruction,
          style: activeStyle,
        });
        setCall((c) => ({
          ...c,
          notes: {
            ...(c.notes || {}),
            [activeStyle]: {
              ...(c.notes?.[activeStyle] || {}),
              content: data.content,
              scores: data.scores ?? null,
              dna_alignment: data.dna_alignment || null,
            },
          },
        }));
        toast.success(data.scores ? "Rewritten and rescored" : "Rewritten");
      }
    } catch (error) {
      toast.error(getActionableError(error, "The note could not be rewritten. Try again."));
    } finally {
      setGenerating(false);
    }
  };

  const copy = async () => {
    const c = call?.notes?.[activeStyle]?.content;
    if (!c) return;
    try {
      await navigator.clipboard.writeText(c);
      toast.success("CRM note copied");
    } catch {
      toast.error("The note could not be copied. Select the text and copy it manually.");
    }
  };

  const download = (format) => {
    const c = call?.notes?.[activeStyle]?.content;
    if (!c) return;
    const text = format === "md" ? `# ${call.title}\n\n${c}` : c;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${call.title || "note"}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteCall = async () => {
    if (demoMode) return;
    setDeleting(true);
    try {
      await api.delete(`/calls/${id}`);
      toast.success("Call deleted");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      const message = error?.response?.data?.detail || "The call could not be deleted";
      toast.error(message);
      setDeleting(false);
    }
  };

  const openDemoRelationship = useCallback(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    navigate(demoRelationshipPath);
  }, [demoRelationshipPath, navigate]);

  const openDemoFlowStep = async (key, options = {}) => {
    if (!demoMode || demoFlowBusy) return;
    const stepIndex = DEMO_FLOW_STEPS.indexOf(key);
    const allowLegacyMobileNavigation = isMobileDemo || options?.allowLocked === true;
    const isRelationshipExploration = key === "relationship";
    if (stepIndex < 0 || (!isRelationshipExploration && !allowLegacyMobileNavigation && stepIndex > demoUnlockedIndex)) return;

    if (isRelationshipExploration) {
      openDemoRelationship();
      return;
    }

    setDemoFlowBusy(key);
    try {
      if (key === "transcript") {
        openDemoSection("transcript");
        return;
      }

      if (key === "analysis") {
        openDemoSection("analysis");
        if (!call?.analysis) {
          await runAnalysis({ navigateToAnalysis: false });
        }
        return;
      }

      if (key === "notes") {
        openDemoSection("notes");
        return;
      }

      if (key === "coaching") {
        if (!Object.values(call?.notes || {}).some((note) => note?.content)) {
          openDemoSection("notes");
          return;
        }
        openDemoSection("coaching");
        if (!call?.coaching) {
          await revealDemoCoaching({ force: true });
        }
      }
    } finally {
      setDemoFlowBusy(null);
    }
  };

  const advanceDemoFlow = (key) => {
    if (!demoMode || demoFlowBusy) return;
    const stepIndex = DEMO_FLOW_STEPS.indexOf(key);
    if (stepIndex < 0) return;
    if (!isMobileDemo) persistDemoUnlockedIndex(stepIndex);
    if (key === "relationship") {
      openDemoRelationship();
      return;
    }
    openDemoSection(key);
  };

  const resetDemo = () => {
    if (!demoMode) return;
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem("callsect:live-demo-stage");
      if (!isMobileDemo) window.sessionStorage.setItem(demoUnlockStorageKey, "0");
    }
    setCall(demoCallForStage(3, demoData));
    setActiveStyle("first_person");
    setDemoStage(3);
    if (!isMobileDemo) setDemoUnlockedIndex(0);
    setDemoCoachingLoading(false);
    setAnalyzing(false);
    setGenerating(false);
    if (isMobileDemo) navigate(demoBasePath);
    else updateTabParam("transcript");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) return (
    <div className={`call-detail-page${demoMode ? " public-demo-page" : ""}`}>
      {demoMode ? <PublicDemoNav useCaseLabel={demoUseCase === "sales" ? "Sales path" : "Retention path"} /> : <Nav />}
      <main className={`call-detail-shell${demoMode ? " call-detail-shell--demo" : ""}`}>
        <div className="call-detail-loading call-detail-loading--polished" role="status" aria-live="polite">
          <div className="call-detail-loading__header"><span /><span /></div>
          <div className="call-detail-loading__tabs"><span /><span /><span /><span /></div>
          <div className="call-detail-loading__body"><span /><span /><span /></div>
          <span className="sr-only">Loading call and relationship context...</span>
        </div>
      </main>
    </div>
  );
  if (!call) return (
    <div className={`call-detail-page${demoMode ? " public-demo-page" : ""}`}>
      {demoMode ? <PublicDemoNav useCaseLabel={demoUseCase === "sales" ? "Sales path" : "Retention path"} /> : <Nav />}
      <main className={`call-detail-shell${demoMode ? " call-detail-shell--demo" : ""}`}>
        <div className="call-detail-not-found">
          <PhoneCall size={28} weight="duotone" />
          <strong>Call not found.</strong>
          <button type="button" onClick={() => navigate("/dashboard")} className="call-detail-secondary-button">Back to calls</button>
        </div>
      </main>
    </div>
  );

  const currentNote = call.notes?.[activeStyle]?.content;
  const hasAnyNote = Boolean(Object.values(call.notes || {}).some((note) => note?.content));
  const activeStyleLabel = STYLES.find((style) => style.key === activeStyle)?.label || "selected style";
  const styleNeedsGeneration = hasAnyNote && !currentNote;
  const analysis = call.analysis;
  const coaching = call.coaching;
  const demoNextAction = demoMode
    ? {
        transcript: {
          label: "Continue to Dissection",
          nextStep: "analysis",
          unlocked: true,
        },
        analysis: {
          label: isMobileDemo ? "See Coaching" : "Continue to Coaching",
          nextStep: "coaching",
          unlocked: Boolean(analysis),
        },
        coaching: {
          label: isMobileDemo ? "See CRM Note" : "Continue to CRM Note",
          nextStep: "notes",
          unlocked: Boolean(coaching),
        },
        notes: {
          label: isMobileDemo ? "See Relationship Memory" : "Continue to Relationship",
          nextStep: "relationship",
          unlocked: hasAnyNote,
        },
      }[activeTab]
    : null;
  const relationshipKey = demoMode
    ? null
    : relationshipContext?.key || location.state?.relationshipKey;
  const relationshipName = relationshipContext?.name || location.state?.relationshipName || call.customer_name;
  const relationshipPath = relationshipKey
    ? `/customers/${encodeURIComponent(relationshipKey)}`
    : null;
  const cameFromRelationship = !demoMode && location.state?.from === "relationship" && relationshipPath;
  const backTarget = demoMode ? "/" : cameFromRelationship ? relationshipPath : "/dashboard";
  const backLabel = demoMode ? "Exit demo" : cameFromRelationship ? "Back to relationship" : "Back to calls";
  const relationshipReturnState = relationshipPath
    ? { activeCallId: call.id }
    : undefined;

  if (shouldUseReducedMobileStory({
    enabled: USE_REDUCED_MOBILE_STORY,
    demoMode,
    isMobile: isMobileDemo,
    section: requestedTab,
  })) {
    return (
      <MobileLiveDemoStory
        call={call}
        demoData={demoData}
        onRestart={resetDemo}
      />
    );
  }

  return (
    <div className={`call-detail-page${demoMode ? " public-demo-page" : ""}`}>
      {demoMode ? <PublicDemoNav useCaseLabel={demoUseCase === "sales" ? "Sales path" : "Retention path"} /> : <Nav />}
      <main className={`call-detail-shell${demoMode ? " call-detail-shell--demo" : ""}`}>
        {!demoMode && (
        <div className="call-detail-context-topbar">

          <Link
            to={backTarget}
            state={relationshipReturnState}
            className="call-detail-back-link"
          >
            <ArrowLeft size={15} weight="bold" />
            {backLabel}
          </Link>
        </div>
        )}

        {demoMode && (
          <Link to="/demo" className="live-demo-back-selection">
            <ArrowLeft size={14} weight="bold" aria-hidden="true" />
            Back to demo selection
          </Link>
        )}

        <header className="call-detail-header">
          <div className="call-detail-header__identity">
            <span className="call-detail-header__mark" aria-hidden="true">
              <PhoneCall size={24} weight="duotone" />
            </span>
            <div className="call-detail-header__copy">
              <div className="call-detail-header__eyebrow">
                {demoMode ? "Synthetic data" : "Conversation intelligence"}
              </div>
              <h1>{call.title}</h1>
              <div className="call-detail-meta">
                {call.customer_name && (
                  relationshipPath ? (
                    <Link
                      to={relationshipPath}
                      state={relationshipReturnState}
                      className="call-detail-meta__relationship"
                    >
                      {call.customer_name}
                    </Link>
                  ) : (
                    <span>{call.customer_name}</span>
                  )
                )}
                {call.company && <span>{call.company}</span>}
                <span>{fmtDateTime(call.occurred_at || call.created_at)}</span>
                <span>{fmt(call.duration)}</span>
              </div>
            </div>
          </div>

          {!demoMode && (
            <div className="call-detail-actions">
              {!analysis && (
                <button
                  onClick={runAnalysis}
                  disabled={analyzing}
                  data-testid="run-analysis-btn"
                  className="call-detail-primary-button"
                >
                  <Target size={16} weight="fill" /> {analyzing ? "Analyzing..." : "Analyze call"}
                </button>
              )}

              <button
                type="button"
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="open-delete-call-dialog"
                className="call-detail-danger-button call-detail-danger-button--header"
              >
                <Trash size={16} weight="fill" />
                Delete call
              </button>
            </div>
          )}
        </header>

        {demoMode ? (
          <div ref={sectionAnchorRef} className="live-demo-flow-anchor">
            <LiveDemoFlowNav
              activeStep={activeTab}
              completedSteps={DEMO_FLOW_STEPS.slice(0, demoUnlockedIndex)}
              maxUnlockedIndex={demoUnlockedIndex}
              busyStep={demoFlowBusy}
              onStepSelect={openDemoFlowStep}
              onRestart={resetDemo}
            />
          </div>
        ) : (
          <CallReadinessBar
            analysisReady={Boolean(analysis)}
            coachingReady={Boolean(coaching)}
            noteReady={hasAnyNote}
            firstResult={firstResult}
            analyzing={analyzing}
            onAnalyze={runAnalysis}
            onOpenSection={openDemoSection}
          />
        )}

        {!demoMode && firstResult && showFirstResultGuide && (
          <FirstResultGuide
            analysisReady={Boolean(analysis)}
            analyzing={analyzing}
            onAnalyze={runAnalysis}
            onOpenSection={openDemoSection}
            onDismiss={() => {
              localStorage.setItem("callsect:first-result-guide-dismissed", "1");
              setShowFirstResultGuide(false);
              trackProductEvent("first_result_guide_dismissed", {
                call_count: Number.isFinite(location.state?.callCount) ? location.state.callCount : 1,
                section: activeTab,
              });
            }}
          />
        )}

        {relationshipPath && (
          <nav className="call-context-navigation" aria-label="Conversation navigation">
            <div className="call-context-navigation__sequence">
              {relationshipContext?.previousCall ? (
                <Link
                  to={`/calls/${relationshipContext.previousCall.call_id}`}
                  state={{
                    from: "relationship",
                    relationshipKey,
                    relationshipName,
                  }}
                  className="call-context-navigation__call"
                  aria-label={`Open previous call: ${relationshipContext.previousCall.title || "Previous call"}`}
                >
                  <ArrowLeft size={19} weight="bold" aria-hidden="true" />
                  <span>
                    <small>Previous call</small>
                    <strong>{relationshipContext.previousCall.title || "Earlier conversation"}</strong>
                    {relationshipContext.previousCall.date && (
                      <em>{fmtDateTime(relationshipContext.previousCall.date)}</em>
                    )}
                  </span>
                </Link>
              ) : (
                <span className="call-context-navigation__call is-disabled">
                  <ArrowLeft size={19} weight="bold" aria-hidden="true" />
                  <span>
                    <small>Previous call</small>
                    <strong>No earlier conversation</strong>
                  </span>
                </span>
              )}

              {relationshipContext?.nextCall ? (
                <Link
                  to={`/calls/${relationshipContext.nextCall.call_id}`}
                  state={{
                    from: "relationship",
                    relationshipKey,
                    relationshipName,
                  }}
                  className="call-context-navigation__call is-next"
                  aria-label={`Open next call: ${relationshipContext.nextCall.title || "Next call"}`}
                >
                  <span>
                    <small>Next call</small>
                    <strong>{relationshipContext.nextCall.title || "Later conversation"}</strong>
                    {relationshipContext.nextCall.date && (
                      <em>{fmtDateTime(relationshipContext.nextCall.date)}</em>
                    )}
                  </span>
                  <ArrowRight size={19} weight="bold" aria-hidden="true" />
                </Link>
              ) : (
                <span className="call-context-navigation__call is-next is-disabled">
                  <span>
                    <small>Next call</small>
                    <strong>No later conversation</strong>
                  </span>
                  <ArrowRight size={19} weight="bold" aria-hidden="true" />
                </span>
              )}
            </div>
          </nav>
        )}

        {!demoMode && deleteDialogOpen && (
          <div className="call-detail-dialog-backdrop" role="dialog" aria-modal="true" aria-labelledby="delete-call-title" data-testid="delete-call-dialog">
            <div className="call-detail-dialog">
              <div className="call-detail-dialog__header">
                <div>
                  <div className="call-detail-dialog__danger"><Trash size={20} weight="fill" /></div>
                  <h2 id="delete-call-title">Delete this call?</h2>
                </div>
                <button type="button" onClick={() => setDeleteDialogOpen(false)} disabled={deleting} aria-label="Close" className="call-detail-dialog__close">
                  <X size={18} />
                </button>
              </div>
              <p><strong>{call.title}</strong>{call.customer_name ? ` for ${call.customer_name}` : ""} will be permanently removed, including its transcript, analysis, CRM notes and coaching.</p>
              <small>This action cannot be undone.</small>
              <div className="call-detail-dialog__actions">
                <button type="button" onClick={() => setDeleteDialogOpen(false)} disabled={deleting} className="call-detail-secondary-button">Cancel</button>
                <button type="button" onClick={deleteCall} disabled={deleting} data-testid="confirm-delete-call" className="call-detail-danger-button">
                  <Trash size={15} weight="fill" /> {deleting ? "Deleting..." : "Delete call"}
                </button>
              </div>
            </div>
          </div>
        )}

        {!demoMode && (
          <LongitudinalDelta
            update={call.relationship_update}
            relationshipPath={relationshipPath}
            relationshipName={relationshipName}
            relationshipReturnState={relationshipReturnState}
            persistenceKey={`${call.id}:${call.relationship_update?.previous_call_id ? "update" : "initial"}`}
          />
        )}

        {!demoMode && (
        <nav ref={sectionAnchorRef} className="call-detail-tabs" aria-label="Call detail sections" role="tablist">
          {[
            { key: "analysis", label: "Dissection", icon: MagnifyingGlass, ready: Boolean(analysis) },
            { key: "notes", label: "CRM Notes", icon: ClipboardText, ready: hasAnyNote },
            { key: "coaching", label: "Coaching", icon: ChartBar, ready: Boolean(coaching) },
            { key: "transcript", label: "Transcript", icon: ChatCircle, ready: Boolean(call.transcript) },
          ].map((tab) => {
            return (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => openDemoSection(tab.key)}
                className={`call-detail-tab${activeTab === tab.key ? " is-active" : ""}${tab.ready ? " is-ready" : ""}`}
                role="tab"
                id={`call-tab-${tab.key}`}
                aria-controls={`call-panel-${tab.key}`}
                aria-selected={activeTab === tab.key}
                tabIndex={activeTab === tab.key ? 0 : -1}
              >
                <tab.icon size={16} weight={activeTab === tab.key ? "fill" : "regular"} />
                <span>{tab.label}</span>
                {tab.ready && <CheckCircle size={12} weight="fill" className="call-detail-tab__ready" aria-label="Ready" />}
              </button>
            );
          })}
        </nav>
        )}

        <div
          ref={sectionContentRef}
          className="call-detail-tab-content"
          key={activeTab}
          role="tabpanel"
          id={`call-panel-${activeTab}`}
          aria-labelledby={`call-tab-${activeTab}`}
        >
          {activeTab === "notes" && (
            <section>
              <div className="call-detail-section-intro call-detail-section-intro--notes">
                <div>
                          <h2>Generate notes in your own writing style.</h2>
                  <p>Choose a format, select the output language, then refine the note without losing your reasoning or tone.</p>
                </div>
              </div>

              <div className="call-detail-notes-grid">
                <aside className="call-detail-style-panel">
                  <div className="call-detail-panel-label">Styles</div>
                  {STYLES.map((style) => {
                    const hasNote = Boolean(call.notes?.[style.key]);
                    return (
                      <button
                        key={style.key}
                        data-testid={`style-${style.key}`}
                        onClick={() => setActiveStyle(style.key)}
                        className={`call-detail-style-button${activeStyle === style.key ? " is-active" : ""}`}
                      >
                        <span>{style.label}</span>
                        {hasNote && <span className="call-detail-style-button__ready" />}
                      </button>
                    );
                  })}
                </aside>

                <section className="call-detail-note-editor">
                  <div className="call-detail-toolbar">
                    <label className="call-detail-language">
                      <span>CRM note language</span>
                      <select value={crmNoteLanguage} onChange={(event) => setCrmNoteLanguage(event.target.value)} data-testid="crm-note-language">
                        <option value="english">English (default)</option>
                        <option value="conversation">Conversation language</option>
                      </select>
                    </label>

                    <button data-testid="generate-note-btn" onClick={() => generate(activeStyle)} disabled={generating} className="call-detail-primary-button call-detail-primary-button--compact">
                      <Sparkle size={14} weight="fill" />
                      {generating ? "Writing..." : currentNote ? "Regenerate" : "Generate"}
                    </button>

                    {currentNote && (
                      <>
                        <RewriteBtn onClick={() => rewrite("shorter")} label="Shorter" disabled={generating} />
                        <RewriteBtn onClick={() => rewrite("more_professional")} label="More professional" disabled={generating} />
                        <RewriteBtn onClick={() => rewrite("more_conversational")} label="More conversational" disabled={generating} />
                        <RewriteBtn onClick={() => rewrite("more_detailed")} label="More detailed" disabled={generating} />
                        <div className="call-detail-toolbar__exports">
                          <button onClick={copy} data-testid="copy-btn" className="call-detail-secondary-button"><Copy size={14} /> Copy</button>
                          <button onClick={() => download("md")} data-testid="download-md-btn" className="call-detail-secondary-button">.md</button>
                          <button onClick={() => download("txt")} data-testid="download-txt-btn" className="call-detail-secondary-button">.txt</button>
                        </div>
                      </>
                    )}
                  </div>


                  {currentNote ? (
                    <>
                      <textarea
                        data-testid="note-textarea"
                        value={currentNote}
                        readOnly
                        aria-readonly="true"
                        spellCheck={false}
                        className="call-detail-note-textarea call-detail-note-textarea--readonly"
                      />
                      <QualityScorePanel scores={call.notes?.[activeStyle]?.scores} />
                    </>
                  ) : (
                    <div className="call-detail-empty-state">
                      <RevealLinesMark />
                      {styleNeedsGeneration ? (
                        <>
                          <strong>Style changed to {activeStyleLabel}.</strong>
                          <p>Generate this style to apply it. Your existing CRM note is still available under its previous style.</p>
                        </>
                      ) : (
                        <>
                          <strong>Ready when you are.</strong>
                          <p>Generate this format and Callsect will write it using your Writing DNA.</p>
                        </>
                      )}
                    </div>
                  )}
                </section>

              </div>
            </section>
          )}

          {activeTab === "transcript" && (
            <section className="call-detail-transcript">
              <div className="call-detail-transcript__header">
                <div>
                  <div className="brand-eyebrow">Conversation record</div>
                  <h2>Transcript</h2>
                </div>
                <span>{(call.segments || []).length ? `${call.segments.length} segments` : "Full text"}</span>
              </div>
              {call.filename && (
                <audio ref={audioRef} onTimeUpdate={(event) => setCurrentTime(event.target.currentTime)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} className="hidden" />
              )}
              <div className="call-detail-transcript__body intel-scrollbar">
                {(call.segments || []).length === 0 ? (
                  <div className="call-detail-transcript__plain">{formatPlainTranscriptSpeakers(call.transcript)}</div>
                ) : (
                  call.segments.map((segment, index) => {
                    const speaker = transcriptSpeakerPresentation(segment.speaker);
                    return (
                      <div
                        key={index}
                        data-transcript-index={index}
                        className={`transcript-row${highlightedTranscriptIndex === index ? " is-highlighted" : ""}`}
                      >
                        <div className="transcript-time">{fmt(segment.start)}</div>
                        <div className="transcript-copy">
                          <span className={`transcript-speaker ${speaker.toneClass}`.trim()}>{speaker.label}</span>
                          <span>{segment.text}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          )}

          {activeTab === "analysis" && (
            <AnalysisView
              analysis={analysis}
              onRun={runAnalysis}
              loading={analyzing}
              findingsStep={findingsStep}
              relationshipPath={relationshipPath}
              relationshipName={relationshipName}
              relationshipReturnState={relationshipReturnState}
              timestampHrefBuilder={transcriptHrefFor}
              deepDiveTarget={deepDiveTarget}
            />
          )}

          {activeTab === "coaching" && (
            demoMode && demoCoachingLoading ? (
              <section className="demo-stage-loading" role="status" aria-live="polite">
                <RevealLinesMark />
                <div>
                  <div className="brand-eyebrow">Preparing coaching</div>
                  <h2>Turning the evidence into a practical next move.</h2>
                  <p>Callsect is connecting the strongest moments from the call to specific coaching guidance.</p>
                </div>
              </section>
            ) : (
              <CoachingView coaching={coaching} onRun={() => revealDemoCoaching({ force: true })} loading={demoCoachingLoading} timestampHrefBuilder={transcriptHrefFor} transcriptSegments={call.segments} />
            )
          )}
        </div>

        {demoMode && demoNextAction && (
          <LiveDemoNextAction
            label={demoNextAction.label}
            disabled={!demoNextAction.unlocked}
            busy={Boolean(demoFlowBusy) || analyzing || generating || demoCoachingLoading}
            onClick={() => advanceDemoFlow(demoNextAction.nextStep)}
          />
        )}
      </main>
    </div>
  );
}

export function MobileLiveDemoStory({
  call,
  demoData,
  onRestart,
  initialScene = 0,
}) {
  const clampScene = (value) => Math.max(0, Math.min(3, Number(value) || 0));
  const [scene, setScene] = useState(() => clampScene(initialScene));
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const touchState = useRef(null);
  const storyStartRef = useRef(null);
  const storyViewportRef = useRef(null);
  const storyTrackRef = useRef(null);
  const totalScenes = 4;

  const businessProblemFinding = demoData.analysis?.deep_dive?.blockers?.[0] || {};
  const hiddenObjectionFinding = demoData.analysis?.deep_dive?.risks?.[0] || {};
  const businessProblem = {
    answer: businessProblemFinding?.finding?.text,
    evidence: businessProblemFinding?.supporting_moments?.[0]?.quote,
    timestamp: businessProblemFinding?.supporting_moments?.[0]?.timestamp,
  };
  const hiddenObjection = {
    answer: hiddenObjectionFinding?.finding?.text,
    evidence: hiddenObjectionFinding?.supporting_moments?.[0]?.quote,
    timestamp: hiddenObjectionFinding?.supporting_moments?.[0]?.timestamp,
  };
  const coachingMove = Array.isArray(demoData.coaching?.coaching_moves)
    ? demoData.coaching.coaching_moves[0] || {}
    : {};
  const situationParts = String(coachingMove.situation || "").split(/\s+-\s+/);
  const situationQuote = situationParts.shift() || "";
  const situationObservation = situationParts.join(" - ");
  const businessProblemSpeaker = coachingSpeakerAtTimestamp(call?.segments, businessProblem.timestamp);
  const hiddenObjectionSpeaker = coachingSpeakerAtTimestamp(call?.segments, hiddenObjection.timestamp);
  const situationSpeaker = coachingSpeakerAtTimestamp(call?.segments, coachingMove.timestamp);
  const note = demoData.noteVariants?.first_person?.default || "";
  const noteParagraphs = note.split(/\n\s*\n/).filter(Boolean).slice(0, 2);

  // Relationship stays inside the same mobile story rather than jumping into
  // the older full Relationship page. Keep it intentionally lite: enough
  // longitudinal context to prove the value, without copying the dense desktop
  // workspace into a narrow swipe card.
  const demoRelationship = demoData.relationship || {};
  const demoMemory = demoRelationship.relationship_memory || {};
  const demoRelationshipState = demoMemory.current_state || {};
  const demoRelationshipFacts = demoRelationshipState.facts || {};
  const demoRelationshipInferences = demoRelationshipState.inferences || {};
  const demoRelationshipHistory = [...(demoRelationship.milestones || [])]
    .sort((left, right) => new Date(right.date).getTime() - new Date(left.date).getTime())
    .slice(0, 3);
  const demoRelationshipBookmark = String(demoRelationshipState.bookmark?.text || "").trim();
  const demoRelationshipSignals = [
    { label: "Buying intent", value: demoRelationshipInferences.buying_intent?.score, invert: false },
    { label: "Churn risk", value: demoRelationshipInferences.churn_risk?.score, invert: true },
    { label: "Trust", value: demoRelationshipInferences.trust?.score, invert: false },
  ].filter((item) => isNumericScore(item.value));
  const demoRelationshipWatch = [
    {
      label: "Blocker",
      value: longitudinalItemText((demoRelationshipFacts.operational_blockers || [])[0]),
      className: "is-blocker",
    },
    {
      label: "Risk",
      value: longitudinalItemText((demoMemory.next_call_brief?.risks || demoRelationshipInferences.risks || [])[0]),
      className: "is-risk",
    },
    {
      label: "Opportunity",
      value: longitudinalItemText((demoMemory.next_call_brief?.opportunities || demoRelationshipInferences.opportunities || [])[0]),
      className: "is-opportunity",
    },
  ].filter((item) => item.value);

  useEffect(() => {
    setScene(clampScene(initialScene));
  }, [initialScene]);

  const scrollToStoryStart = useCallback((behavior = "smooth") => {
    window.requestAnimationFrame(() => {
      const target = storyStartRef.current;
      if (!target) return;
      const targetTop = window.scrollY + target.getBoundingClientRect().top - 12;
      window.scrollTo({ top: Math.max(0, targetTop), behavior });
    });
  }, []);

  const moveTo = useCallback((nextScene) => {
    const normalized = Math.max(0, Math.min(totalScenes - 1, nextScene));
    setShowSwipeHint(false);
    setScene(normalized);
    scrollToStoryStart("smooth");
  }, [scrollToStoryStart]);

  const restart = () => {
    onRestart();
    setScene(0);
    setShowSwipeHint(true);
    touchState.current = null;
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    const now = performance.now();
    setShowSwipeHint(false);
    touchState.current = {
      x: touch.clientX,
      y: touch.clientY,
      axis: null,
      lastX: touch.clientX,
      lastTime: now,
      velocityX: 0,
    };
  };

  const onTouchMove = (event) => {
    const gesture = touchState.current;
    const touch = event.touches?.[0];
    if (!gesture || !touch) return;

    const deltaX = touch.clientX - gesture.x;
    const deltaY = touch.clientY - gesture.y;

    if (!gesture.axis) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 7) return;
      gesture.axis = Math.abs(deltaX) > Math.abs(deltaY) ? "x" : "y";
      if (gesture.axis === "x") storyTrackRef.current?.classList.add("is-dragging");
    }

    if (gesture.axis !== "x") return;

    const now = performance.now();
    const elapsed = Math.max(1, now - gesture.lastTime);
    gesture.velocityX = (touch.clientX - gesture.lastX) / elapsed;
    gesture.lastX = touch.clientX;
    gesture.lastTime = now;

    let nextDrag = deltaX;
    if ((scene === 0 && nextDrag > 0) || (scene === totalScenes - 1 && nextDrag < 0)) {
      nextDrag *= 0.28;
    }

    if (storyTrackRef.current) {
      storyTrackRef.current.style.transform = `translate3d(calc(-${scene * 100}% + ${nextDrag}px), 0, 0)`;
    }
  };

  const finishTouchGesture = (event) => {
    const gesture = touchState.current;
    const touch = event.changedTouches?.[0];
    touchState.current = null;

    if (!gesture || !touch || gesture.axis !== "x") {
      storyTrackRef.current?.classList.remove("is-dragging");
      if (storyTrackRef.current) {
        storyTrackRef.current.style.transform = `translate3d(-${scene * 100}%, 0, 0)`;
      }
      return;
    }

    const deltaX = touch.clientX - gesture.x;
    const width = storyViewportRef.current?.clientWidth || 360;
    const distanceThreshold = Math.min(88, width * 0.2);
    const velocityThreshold = 0.42;

    let targetScene = scene;
    if (deltaX <= -distanceThreshold || gesture.velocityX <= -velocityThreshold) {
      targetScene = Math.min(totalScenes - 1, scene + 1);
    } else if (deltaX >= distanceThreshold || gesture.velocityX >= velocityThreshold) {
      targetScene = Math.max(0, scene - 1);
    }

    storyTrackRef.current?.classList.remove("is-dragging");
    if (storyTrackRef.current) {
      storyTrackRef.current.style.transform = `translate3d(-${targetScene * 100}%, 0, 0)`;
    }
    if (targetScene !== scene) moveTo(targetScene);
  };

  const cancelTouchGesture = () => {
    touchState.current = null;
    storyTrackRef.current?.classList.remove("is-dragging");
    if (storyTrackRef.current) {
      storyTrackRef.current.style.transform = `translate3d(-${scene * 100}%, 0, 0)`;
    }
  };


  const sceneMeta = [
    { number: "01", label: "Understand", icon: MagnifyingGlass },
    { number: "02", label: "Improve", icon: Target },
    { number: "03", label: "Act", icon: ClipboardText },
    { number: "04", label: "Remember", icon: Users },
  ];

  const renderSceneContent = (sceneIndex) => {
    if (sceneIndex === 0) {
      return (
        <>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-300">Evidence-backed dissection</div>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight">Know what happened. Understand what it meant.</h2>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-400">
            No summary wall. Start with the commercial problem and the exact moment that supports it.
          </p>

          <div className="mt-5 space-y-3">
            <article className="live-demo-mobile-proof">
              <small>Business problem</small>
              <strong>{localizeSellerRole(businessProblem.answer || "The customer needs a defensible business case before they can commit.")}</strong>
              {businessProblem.evidence && (
                <blockquote>
                  <span className="live-demo-mobile-evidence-meta">
                    {businessProblemSpeaker?.label && (
                      <span className={`transcript-speaker landing-coaching-evidence__speaker ${businessProblemSpeaker.toneClass}`.trim()}>
                        {businessProblemSpeaker.label}
                      </span>
                    )}
                    {businessProblem.timestamp && <time>{businessProblem.timestamp}</time>}
                  </span>
                  <span>“{localizeSellerRole(businessProblem.evidence)}”</span>
                </blockquote>
              )}
            </article>
            <article className="live-demo-mobile-proof is-secondary">
              <small>Risk beneath the surface</small>
              <strong>{localizeSellerRole(hiddenObjection.answer || "Positive sentiment is masking an unresolved decision risk.")}</strong>
              {hiddenObjection.evidence && (
                <blockquote>
                  <span className="live-demo-mobile-evidence-meta">
                    {hiddenObjectionSpeaker?.label && (
                      <span className={`transcript-speaker landing-coaching-evidence__speaker ${hiddenObjectionSpeaker.toneClass}`.trim()}>
                        {hiddenObjectionSpeaker.label}
                      </span>
                    )}
                    {hiddenObjection.timestamp && <time>{hiddenObjection.timestamp}</time>}
                  </span>
                  <span>“{localizeSellerRole(hiddenObjection.evidence)}”</span>
                </blockquote>
              )}
            </article>
          </div>
        </>
      );
    }

    if (sceneIndex === 1) {
      return (
        <>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-600 dark:text-violet-300">Coaching move</div>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight">
            {localizeSellerRole(coachingMove.title || "Turn the blocker into a better decision path")}
          </h2>

          <div className="mt-5 space-y-3 live-demo-mobile-coaching-fields">
            <div className="landing-coaching-field">
              <span>Situation</span>
              <div className="landing-coaching-evidence-block">
                <span className="landing-coaching-evidence live-demo-mobile-coaching-evidence">
                  <span className="live-demo-mobile-evidence-meta">
                    {situationSpeaker?.label && (
                      <span className={`transcript-speaker landing-coaching-evidence__speaker ${situationSpeaker.toneClass}`.trim()}>
                        {situationSpeaker.label}
                      </span>
                    )}
                    {coachingMove.timestamp && <time>{coachingMove.timestamp}</time>}
                  </span>
                  <span className="landing-coaching-field__quote">{localizeSellerRole(situationQuote)}</span>
                </span>
                {situationObservation && (
                  <p className="landing-coaching-field__observation">
                    {localizeSellerRole(situationObservation)}
                  </p>
                )}
              </div>
            </div>

            <div className="landing-coaching-field">
              <span>Better approach</span>
              <div>
                <p>{localizeSellerRole(coachingMove.better_move || "Diagnose the decision risk before positioning the solution.")}</p>
              </div>
            </div>

            <div className="landing-coaching-field is-emphasized">
              <span>Example wording</span>
              <div>
                <blockquote>“{localizeSellerRole(coachingMove.example_phrase || "What would need to be true for this to become a real priority?")}”</blockquote>
              </div>
            </div>

            <div className="landing-coaching-field">
              <span>Why it works</span>
              <div>
                <p>{localizeSellerRole(coachingMove.why_it_works || "It makes the real decision criterion explicit before the conversation drifts back to features.")}</p>
              </div>
            </div>
          </div>
        </>
      );
    }

    if (sceneIndex === 2) {
      return (
        <>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-300">After the call</div>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight">Ready for the CRM. Still sounds like you.</h2>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-400">
            The same understanding becomes documentation shaped by Writing DNA—not generic AI prose.
          </p>

          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700 dark:border-white/[0.08] dark:bg-white/[0.025] dark:text-slate-300">
            {noteParagraphs.map((paragraph, index) => (
              <p key={index} className={index ? "mt-4" : ""}>{paragraph}</p>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-100">
            <CheckCircle size={19} weight="fill" className="shrink-0" />
            <span>Useful after the call. The bigger payoff is what Callsect carries into the next one.</span>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-600 dark:text-violet-300">Relationship intelligence</div>
        <h2 className="mt-3 text-2xl font-semibold tracking-tight">Carry the customer context into what comes next.</h2>
        <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-400">
          One call explains the moment. Relationship Memory keeps the history, current state, and next risk connected across conversations.
        </p>

        <div className="live-demo-mobile-relationship-lite mt-5 space-y-3">
          <section className="live-demo-mobile-relationship-lite__history rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/[0.08] dark:bg-white/[0.025]">
            <div className="flex items-center justify-between gap-3">
              <strong className="live-demo-mobile-relationship-lite__section-title">Conversation history</strong>
              <span className="text-xs text-slate-400">{demoRelationshipHistory.length} calls</span>
            </div>
            <div className="mt-3 space-y-2.5">
              {demoRelationshipHistory.map((milestone, index) => (
                <div key={milestone.call_id || index} className="live-demo-mobile-relationship-lite__call flex items-center gap-3">
                  <span className={`live-demo-mobile-relationship-lite__dot${index === 0 ? " is-current" : ""}`} aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{milestone.title || "Customer call"}</div>
                    <div className="mt-0.5 text-[11px] text-slate-400">{fmtDateTime(milestone.date)}</div>
                    {index === 0 && milestone.follow_up_email?.subject && milestone.follow_up_email?.body ? (
                      <div className="live-demo-mobile-relationship-lite__follow-up-row">
                        <FollowUpEmail
                          callId={milestone.call_id}
                          customerName={demoRelationship.customer?.name || ""}
                          available
                          demoEmail={milestone.follow_up_email}
                          demoReadOnly
                          triggerClassName="relationship-story-card__follow-up live-demo-mobile-relationship-lite__follow-up"
                        />
                      </div>
                    ) : null}
                  </div>
                  {index === 0 && <span className="live-demo-mobile-relationship-lite__current">Latest</span>}
                </div>
              ))}
            </div>
          </section>

          <section className="live-demo-mobile-relationship-lite__state rounded-2xl border border-violet-200 bg-violet-50/70 p-4 dark:border-violet-400/20 dark:bg-violet-400/[0.07]">
            <div className="live-demo-mobile-relationship-lite__section-title">Current relationship state</div>
            {demoRelationshipBookmark && (
              <p className="mt-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100">{demoRelationshipBookmark}</p>
            )}
            {demoRelationshipSignals.length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {demoRelationshipSignals.map((signal) => (
                  <div key={signal.label} className="live-demo-mobile-relationship-lite__signal rounded-xl border border-slate-200 bg-white/80 px-2.5 py-2 dark:border-white/[0.08] dark:bg-black/15">
                    <small className="live-demo-mobile-relationship-lite__signal-label block text-[9px] font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400">{signal.label}</small>
                    <strong className={`live-demo-mobile-relationship-lite__signal-value block text-base score-text--${scoreToTone(signal.value, { invert: signal.invert })}`}>{scoreToRating(signal.value)}</strong>
                  </div>
                ))}
              </div>
            )}
          </section>

          {demoRelationshipWatch.length > 0 && (
            <section className="live-demo-mobile-relationship-lite__watch rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/[0.08] dark:bg-white/[0.025]">
              <strong className="live-demo-mobile-relationship-lite__section-title">What to watch next</strong>
              <div className="mt-3 space-y-3">
                {demoRelationshipWatch.map((item) => (
                  <div key={item.label} className={`live-demo-mobile-relationship-lite__watch-item ${item.className}`}>
                    <span>{item.label}</span>
                    <p>{item.value}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-2xl border border-violet-200 bg-violet-50/70 p-4 dark:border-violet-400/20 dark:bg-violet-400/[0.07]">
            <strong className="text-sm">Found this useful?</strong>
            <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-400">Try it with one of your own calls.</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Link
                to="/login?mode=register"
                className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-violet-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-800 dark:bg-violet-600 dark:hover:bg-violet-500"
              >
                Sign up free
                <ArrowRight size={15} weight="bold" aria-hidden="true" />
              </Link>
              <button
                type="button"
                onClick={restart}
                className="inline-flex min-h-10 items-center rounded-xl px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300"
              >
                Restart demo
              </button>
            </div>
          </section>
        </div>
      </>
    );
  };

  return (
    <div className="live-demo-mobile-page min-h-screen bg-slate-50 text-slate-950 dark:bg-[#050507] dark:text-white">
      <header className="public-demo-header" aria-label="Callsect interactive demo">
        <div className="public-demo-header__inner">
          <Link to="/" className="brand-lockup-link" aria-label="Back to Callsect website">
            <BrandLockup className="brand-lockup--landing" variant="auto" />
          </Link>
          <div className="public-demo-header__actions">
            <PublicThemeControl />
            <Link to="/" className="public-demo-header__back" aria-label="Back to homepage">
              <ArrowLeft size={14} weight="bold" aria-hidden="true" />
              <span>Back to homepage</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto flex min-h-[calc(100vh-57px)] w-full max-w-md flex-col px-4 pb-6 pt-4">
        <div className="live-demo-mobile-context-row mb-4">
          <span className="live-demo-mobile-context-badge">Interactive demo</span>
          <Link to="/demo" className="live-demo-mobile-back-selection">
            <ArrowLeft size={13} weight="bold" aria-hidden="true" />
            Back to demo selection
          </Link>
        </div>

        <section className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#0b0b10]">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">
              <PhoneCall size={20} weight="duotone" />
            </span>
            <div className="min-w-0">
              <div className="text-[12px] font-semibold uppercase tracking-[0.22em] text-indigo-600 dark:text-indigo-300">
                Synthetic data
              </div>
              <h1 className="mt-1 truncate text-lg font-semibold">{call?.title || "Customer conversation"}</h1>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                <span>{call?.customer_name}</span>
                <span>{call?.company}</span>
                <span>{fmt(call?.duration)}</span>
              </div>
            </div>
          </div>
        </section>

        <div ref={storyStartRef} className="mb-4 flex items-center gap-2" aria-label={`Step ${scene + 1} of ${totalScenes}`}>
          {sceneMeta.map((item, index) => (
            <button
              key={item.label}
              type="button"
              onClick={() => moveTo(index)}
              aria-label={`Open ${item.label}`}
              className={`h-1.5 flex-1 rounded-full transition ${
                index <= scene
                  ? "bg-indigo-600 dark:bg-indigo-400"
                  : "bg-slate-200 dark:bg-white/10"
              }`}
            />
          ))}
        </div>

        <div
          ref={storyViewportRef}
          className="live-demo-mobile-story-viewport flex flex-1 overflow-hidden"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={finishTouchGesture}
          onTouchCancel={cancelTouchGesture}
        >
          <div
            ref={storyTrackRef}
            className={`live-demo-mobile-story-track${showSwipeHint && scene === 0 ? " is-hinting" : ""}`}
            style={{ transform: `translate3d(-${scene * 100}%, 0, 0)` }}
            onAnimationEnd={(event) => {
              if (event.currentTarget === event.target) setShowSwipeHint(false);
            }}
          >
            {sceneMeta.map((item, index) => {
              const SlideIcon = item.icon;
              return (
                <section
                  key={item.label}
                  aria-hidden={index !== scene}
                  className="live-demo-mobile-story-card live-demo-mobile-story-slide flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.10)] dark:border-white/10 dark:bg-[#0a0a0f] dark:shadow-[0_18px_60px_rgba(0,0,0,0.35)]"
                >
                  <div className="border-b border-slate-100 px-5 py-4 dark:border-white/[0.07]">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-300">
                        <SlideIcon size={15} weight="fill" />
                        {item.number} · {item.label}
                      </div>
                      <span className="text-xs text-slate-400">Swipe ↔</span>
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col p-5">
                    {renderSceneContent(index)}
                  </div>
                </section>
              );
            })}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => moveTo(scene - 1)}
            disabled={scene === 0}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 disabled:opacity-30 dark:text-slate-300"
          >
            <ArrowLeft size={15} weight="bold" />
            Back
          </button>
          <span className="text-xs text-slate-400">Swipe or tap through</span>
          <button
            type="button"
            onClick={() => moveTo(scene + 1)}
            disabled={scene === totalScenes - 1}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 disabled:opacity-30 dark:text-slate-300"
          >
            Next
            <ArrowRight size={15} weight="bold" />
          </button>
        </div>
      </main>

    </div>
  );
}
function FirstResultGuide({ analysisReady, analyzing, onAnalyze, onOpenSection, onDismiss }) {
  const steps = [
    {
      key: "analysis",
      number: "1",
      title: "Start with the Dissection",
      copy: "Check what changed, the customer’s real concern, and the evidence behind each conclusion.",
      icon: MagnifyingGlass,
    },
    {
      key: "notes",
      number: "2",
      title: "Document the call in your voice",
      copy: "Generate a CRM-ready note from the same relationship context without flattening your reasoning or tone.",
      icon: ClipboardText,
    },
    {
      key: "coaching",
      number: "3",
      title: "Improve the next move",
      copy: "Open Coaching for guidance addressed directly to you and grounded in the exact words from this call.",
      icon: ChartBar,
    },
  ];

  return (
    <section className="first-result-guide" aria-label="First result guide">
      <div className="first-result-guide__header">
        <div>
          <div className="brand-eyebrow">Your first Callsect result</div>
          <strong>Review it in this order.</strong>
          <p>Three quick passes are enough to understand the call, document it in your voice, and improve what happens next.</p>
        </div>
        <button type="button" onClick={onDismiss} aria-label="Dismiss first result guide" title="Dismiss guide">
          <X size={17} />
        </button>
      </div>

      <div className="first-result-guide__steps">
        {steps.map(({ key, number, title, copy, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              if (key === "analysis" && !analysisReady) onAnalyze();
              else onOpenSection(key);
            }}
            disabled={analyzing || (!analysisReady && key !== "analysis")}
          >
            <span className="first-result-guide__number">{number}</span>
            <span className="first-result-guide__icon" aria-hidden="true"><Icon size={19} weight="duotone" /></span>
            <span>
              <strong>{title}</strong>
              <small>{copy}</small>
            </span>
            <ArrowRight size={15} weight="bold" aria-hidden="true" />
          </button>
        ))}
      </div>
    </section>
  );
}

function CallReadinessBar({
  analysisReady,
  coachingReady,
  noteReady,
  firstResult,
  analyzing,
  onAnalyze,
  onOpenSection,
  sequential = false,
}) {
  const readyCount = [analysisReady, coachingReady, noteReady].filter(Boolean).length;

  return (
    <section className={`call-readiness-bar${firstResult ? " is-first-result" : ""}`} aria-label="Call output readiness">
      <div className="call-readiness-bar__copy">
        <span className="call-readiness-bar__mark" aria-hidden="true">
          {analysisReady ? <CheckCircle size={21} weight="fill" /> : <Sparkle size={21} weight="duotone" />}
        </span>
        <div>
          <strong>{analysisReady ? "Your call is ready to use." : "The transcript is safe. Complete the dissection."}</strong>
          <p>
            {analysisReady
              ? "Start with what changed, document the call in your voice, then improve the next move."
              : "Run the analysis to turn the conversation into a Dissection, relationship context, and practical outputs."}
          </p>
        </div>
      </div>

      <div className="call-readiness-bar__outputs">
        <button type="button" className={analysisReady ? "is-ready" : ""} onClick={() => analysisReady ? onOpenSection("analysis") : onAnalyze()} disabled={analyzing}>
          <MagnifyingGlass size={15} weight="duotone" />
          <span><small>1</small> Dissection</span>
          {analysisReady && <CheckCircle size={13} weight="fill" />}
        </button>
        <button
          type="button"
          className={noteReady ? "is-ready" : ""}
          onClick={() => onOpenSection("notes")}
          disabled={!analysisReady}
        >
          <ClipboardText size={15} weight="duotone" />
          <span><small>2</small> CRM note</span>
          {noteReady && <CheckCircle size={13} weight="fill" />}
        </button>
        <button
          type="button"
          className={coachingReady ? "is-ready" : ""}
          onClick={() => onOpenSection("coaching")}
          disabled={sequential ? !noteReady : !analysisReady}
        >
          <ChartBar size={15} weight="duotone" />
          <span><small>3</small> Coaching</span>
          {coachingReady && <CheckCircle size={13} weight="fill" />}
        </button>
      </div>

      <span className="call-readiness-bar__count">{readyCount}/3 ready</span>
    </section>
  );
}

function RewriteBtn({ onClick, label, disabled = false }) {
  return (
    <button disabled={disabled} onClick={onClick} data-testid={`rewrite-${label.toLowerCase().replace(/\s+/g, "-")}`} className="call-detail-secondary-button">
      <ArrowClockwise size={12} /> {label}
    </button>
  );
}

const SIGNAL_DESCRIPTIONS = {
  buyingIntent: "How strongly the conversation suggests a willingness to buy, renew, or move forward.",
  trust: "How much confidence the customer shows in the rep, company, and proposed solution.",
  relationship: "The overall strength and stability of the customer relationship.",
  confidence: "How certain and decisive the customer appears about their position.",
  churnRisk: "The likelihood that the customer may downgrade, cancel, or not renew.",
  quality: "The overall commercial quality and effectiveness of the conversation.",
};

const SKILL_DESCRIPTIONS = {
  empathy: "How well the rep acknowledges the customer's perspective and emotions.",
  listening: "How effectively the rep understands and responds to what the customer actually says.",
  conversationControl: "How well the rep guides the discussion toward a useful outcome without dominating it.",
};

function scoreTone(value, invert = false) {
  return scoreToTone(value, { invert });
}

function Bar({ label, value, description, reason, directionNote, invert = false }) {
  const hasScore = isNumericScore(value);
  const normalized = hasScore ? Math.max(0, Math.min(100, Number(value))) : null;
  const tone = hasScore ? scoreTone(normalized, invert) : "";

  return (
    <div className={`findings-score${tone ? ` findings-score--${tone}` : ""}`}>
      <div className="findings-score__head">
        <div className="findings-score__label">
          <span className="findings-score__label-text">{label}</span>
        </div>
        <ScoreInsight label={label} reason={reason} description={description} directionNote={directionNote} />
      </div>
      <div className="score-meter">
        <div
          className={`score-meter__fill${tone ? ` score-meter__fill--${tone}` : ""}`}
          style={{ "--score-meter-scale": hasScore ? normalized / 100 : 0 }}
        />
      </div>
    </div>
  );
}

function renderWithItalicQuotes(value, quoteClassName) {
  const text = String(value ?? "");
  const parts = text.split(/(“[^”]*”|"[^"]*")/g);
  return parts.map((part, index) => {
    const isQuote = /^(“[^”]*”|"[^"]*")$/.test(part);
    return isQuote ? <em key={`${index}-${part}`} className={quoteClassName || undefined}>{part}</em> : part;
  });
}

function splitLeadingEvidenceQuote(value) {
  const text = String(value ?? "").trim();
  const match = text.match(/^(“[^”]*”|"[^"]*")\s*(?:—|–|-)\s*(.*)$/s);
  if (!match) return { quote: "", commentary: text };
  return { quote: match[1], commentary: String(match[2] || "").trim() };
}

function splitQuestionGuidance(value) {
  const text = String(value ?? "").trim();
  if (!text) return { evidenceQuote: "", lead: "", suggestion: "" };

  // Coaching may optionally start with transcript evidence followed by an em dash.
  // Keep that quote visually distinct from the future wording Callsect suggests.
  let evidenceQuote = "";
  let guidance = text;
  const evidence = splitLeadingEvidenceQuote(text);
  if (evidence.quote) {
    evidenceQuote = evidence.quote;
    guidance = evidence.commentary;
  }

  // Framing labels are intentionally language-agnostic: any concise leading clause ending
  // in a colon becomes its own line (e.g. "In un prossimo confronto:").
  const labelMatch = String(guidance || "").match(/^([^:\n]{1,120}:)\s*(.*)$/s);
  if (labelMatch) {
    return {
      evidenceQuote,
      lead: String(labelMatch[1] || "").trim(),
      suggestion: String(labelMatch[2] || "").trim(),
    };
  }

  // Legacy items may contain only a framing label plus a quoted suggested question.
  // They still belong to guidance, not transcript evidence, unless an evidence dash was found above.
  return { evidenceQuote, lead: "", suggestion: String(guidance || "").trim() };
}

function formatSuggestedQuestion(value) {
  let text = String(value ?? "").trim();
  if (!text) return "";

  // Suggested future wording is guidance, not transcript evidence. Normalize only
  // its presentation so legacy/new outputs share the same visual grammar.
  text = text.replace(/^[\s“”"'‘’]+|[\s“”"'‘’]+$/g, "").trim();
  if (!text) return "";

  const chars = Array.from(text);
  const firstLetterIndex = chars.findIndex((char) => /\p{L}/u.test(char));
  if (firstLetterIndex >= 0) {
    chars[firstLetterIndex] = chars[firstLetterIndex].toLocaleUpperCase();
  }

  return `“${chars.join("")}”`;
}

function CoachingQuestionPrompt({ value, confidence, testId }) {
  const { evidenceQuote, lead, suggestion } = splitQuestionGuidance(value);
  const formattedSuggestion = formatSuggestedQuestion(suggestion);

  return (
    <span className="coaching-question-prompt">
      {evidenceQuote && (
        <span className="coaching-question-prompt__evidence">
          <em className="evidence-verbatim-quote">{evidenceQuote}</em>
          {!lead && !suggestion && <ConfChip confidence={confidence} testId={testId} inline />}
        </span>
      )}
      {lead && <span className="coaching-question-prompt__lead">{lead}</span>}
      {formattedSuggestion ? (
        <span className="coaching-question-prompt__suggestion">
          <em>{formattedSuggestion}</em>
          <ConfChip confidence={confidence} testId={testId} inline />
        </span>
      ) : (!evidenceQuote && lead ? (
        <span className="coaching-question-prompt__lead-only">
          <ConfChip confidence={confidence} testId={testId} inline />
        </span>
      ) : null)}
    </span>
  );
}

function CoachingEvidenceCopy({ value, confidence, testId }) {
  const { quote, commentary } = splitLeadingEvidenceQuote(value);
  if (!quote) {
    return (
      <span className="coaching-evidence__copy">
        <span className="coaching-evidence__commentary">{value}<ConfChip confidence={confidence} testId={testId} inline /></span>
      </span>
    );
  }

  return (
    <span className="coaching-evidence__copy">
      <span className="coaching-evidence__quote-line">
        <em className="evidence-verbatim-quote coaching-evidence__quote">{quote}</em>
        {!commentary && <ConfChip confidence={confidence} testId={testId} inline />}
      </span>
      {commentary && (
        <span className="coaching-evidence__commentary">{renderWithItalicQuotes(commentary)}<ConfChip confidence={confidence} testId={testId} inline /></span>
      )}
    </span>
  );
}

function CoachingTimestamp({ value, href }) {
  const timestamp = String(value || "").trim();
  if (!timestamp) return null;
  const seconds = parseTimestampSeconds(timestamp);
  if (href && Number.isFinite(seconds)) {
    return (
      <Link
        to={href}
        className="coaching-evidence__timestamp"
        aria-label={`Open transcript at ${timestamp}`}
        title="Open this moment in the transcript"
      >
        {timestamp}
      </Link>
    );
  }
  return <span className="coaching-evidence__timestamp is-static">{timestamp}</span>;
}

function CoachingSpeaker({ speaker }) {
  if (!speaker?.label || !speaker?.toneClass) return null;
  return (
    <span className={`transcript-speaker coaching-evidence__speaker ${speaker.toneClass}`.trim()}>
      {speaker.label}
    </span>
  );
}

function CoachingEvidenceMeta({ speaker, timestamp, href, placeholder = false }) {
  const hasTimestamp = Boolean(String(timestamp || "").trim());
  if (!hasTimestamp && !placeholder) return null;
  return (
    <span className={`coaching-evidence__meta${hasTimestamp ? "" : " is-placeholder"}`}>
      {hasTimestamp && <CoachingSpeaker speaker={speaker} />}
      {hasTimestamp ? (
        <CoachingTimestamp value={timestamp} href={href} />
      ) : (
        <span className="coaching-evidence__timestamp coaching-evidence__timestamp--placeholder" aria-hidden="true" />
      )}
    </span>
  );
}

function List({ title, items, icon: Icon, color = "text-slate-300", testIdPrefix, italicQuotes = false, showTimestamps = false, timestampHrefBuilder, speakerResolver, questionPrompt = false }) {
  if (!Array.isArray(items) || items.length === 0) return null;

  // Questions to Ask can mix evidence-grounded prompts with forward-looking prompts that
  // do not need transcript evidence. Keep grounded items first, then place no-timestamp
  // items last while preserving the model's original order within each group.
  const displayItems = items
    .map((item, originalIndex) => ({
      item,
      originalIndex,
      hasTimestamp: Boolean(String(item?.timestamp || "").trim()),
    }))
    .sort((a, b) => {
      if (!questionPrompt || a.hasTimestamp === b.hasTimestamp) return a.originalIndex - b.originalIndex;
      return a.hasTimestamp ? -1 : 1;
    });

  return (
    <div className="findings-list">
      <div className="findings-list__title">{Icon && <Icon size={13} weight="fill" />} {title}</div>
      <ul>
        {displayItems.map(({ item, originalIndex, hasTimestamp }, index) => {
          const { text, confidence } = itemParts(item);
          const speaker = hasTimestamp && typeof speakerResolver === "function"
            ? speakerResolver(item?.timestamp)
            : null;
          const evidenceLineClass = (showTimestamps || italicQuotes)
            ? `coaching-evidence__line${showTimestamps && (hasTimestamp || questionPrompt) ? " coaching-evidence__line--with-meta" : ""}${questionPrompt ? " coaching-evidence__line--question" : ""}`
            : undefined;
          return (
            <li key={originalIndex} className={color} data-testid={testIdPrefix ? `${testIdPrefix}-${index}` : undefined}>
              <span className="findings-list__bullet" />
              <span className={evidenceLineClass}>
                {showTimestamps && (hasTimestamp || questionPrompt) && (
                  <CoachingEvidenceMeta
                    speaker={speaker}
                    timestamp={hasTimestamp ? item?.timestamp : null}
                    href={hasTimestamp && typeof timestampHrefBuilder === "function" ? timestampHrefBuilder(item?.timestamp) : null}
                    placeholder={questionPrompt && !hasTimestamp}
                  />
                )}
                {questionPrompt ? (
                  <CoachingQuestionPrompt
                    value={text}
                    confidence={confidence}
                    testId={testIdPrefix ? `${testIdPrefix}-${index}-conf` : undefined}
                  />
                ) : italicQuotes ? (
                  <CoachingEvidenceCopy
                    value={text}
                    confidence={confidence}
                    testId={testIdPrefix ? `${testIdPrefix}-${index}-conf` : undefined}
                  />
                ) : (
                  <span>{text}<ConfChip confidence={confidence} testId={testIdPrefix ? `${testIdPrefix}-${index}-conf` : undefined} inline /></span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function CompactSignal({ label, value, invert = false }) {
  const numeric = Math.max(0, Math.min(100, Number(value) || 0));
  const tone = scoreToTone(numeric, { invert });
  return (
    <div className={`findings-signal-pill findings-signal-pill--${tone}`}>
      <span>{label}</span>
      <strong className="score-grade">{scoreToRating(numeric)}</strong>
    </div>
  );
}

function FindingGroup({ title, items, icon: Icon, tone = "violet", emptyText }) {
  const visible = Array.isArray(items) ? items.filter((item) => itemParts(item).text) : [];
  if (!visible.length && !emptyText) return null;
  return (
    <div className={`findings-classified-group findings-classified-group--${tone}`}>
      <div className="findings-classified-group__title">
        {Icon && <Icon size={14} weight="fill" aria-hidden="true" />}
        {title}
      </div>
      {visible.length ? (
        <ul>
          {visible.map((item, index) => {
            const { text, confidence } = itemParts(item);
            return (
              <li key={index}>
                <span className="findings-list__bullet" />
                <span>{text}<ConfChip confidence={confidence} inline /></span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="findings-classified-group__empty">{emptyText}</p>
      )}
    </div>
  );
}

function ActionList({ actions }) {
  const visible = Array.isArray(actions) ? actions.filter((item) => itemParts(item).text) : [];
  if (!visible.length) return null;
  const ownerLabel = {
    customer: "Customer",
    sales_rep: "Rep",
    other: "Other",
    unknown: "Owner unclear",
  };
  return (
    <ul className="findings-action-list">
      {visible.map((action, index) => {
        const text = action?.text || action?.what || itemParts(action).text;
        const owner = action?.owner || action?.who || "unknown";
        const deadline = action?.deadline;
        return (
          <li key={index}>
            <span className={`findings-owner-chip findings-owner-chip--${owner}`}>
              {ownerLabel[owner] || ownerLabel.unknown}
            </span>
            <span className="findings-action-list__copy">
              <strong>{localizeSellerRole(text)}</strong>
              {deadline && String(deadline).toLowerCase() !== "not established" && (
                <small>{deadline}</small>
              )}
            </span>
            <ConfChip confidence={action?.confidence} />
          </li>
        );
      })}
    </ul>
  );
}

function parseTimestampSeconds(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const match = raw.match(/^(?:(\d{1,2}):)?(\d{1,2}):(\d{2})$/);
  if (!match) return null;

  const hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);
  const seconds = Number(match[3] || 0);
  if (minutes > 59 || seconds > 59) return null;

  return (hours * 3600) + (minutes * 60) + seconds;
}

function coachingSpeakerAtTimestamp(segments, timestamp) {
  const seconds = parseTimestampSeconds(timestamp);
  if (!Number.isFinite(seconds) || !Array.isArray(segments)) return null;

  const segment = segments.find((item) => (
    item && Math.floor(Number(item.start || 0)) === seconds
  ));
  if (!segment?.speaker) return null;

  const presentation = transcriptSpeakerPresentation(segment.speaker);
  return presentation.role === "rep" || presentation.role === "client" ? presentation : null;
}

function normalizeEvidenceText(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function evidenceDescriptionIsRedundant(description, findingText, quote) {
  const descriptionText = normalizeEvidenceText(description);
  if (!descriptionText) return true;

  const finding = normalizeEvidenceText(findingText);
  const evidenceQuote = normalizeEvidenceText(quote);
  if ((finding && (descriptionText === finding || finding.includes(descriptionText) || descriptionText.includes(finding)))
    || (evidenceQuote && (descriptionText === evidenceQuote || evidenceQuote.includes(descriptionText)))) {
    return true;
  }

  const descriptionTokens = new Set(descriptionText.split(" ").filter((token) => token.length >= 4));
  const findingTokens = new Set(finding.split(" ").filter((token) => token.length >= 4));
  if (!descriptionTokens.size || !findingTokens.size) return false;
  const shared = [...descriptionTokens].filter((token) => findingTokens.has(token)).length;
  return shared / Math.min(descriptionTokens.size, findingTokens.size) >= 0.78;
}

function KeyMomentList({ items, timestampHrefBuilder, findingText = "" }) {
  const visible = Array.isArray(items)
    ? items.filter((item) => {
        const timestamp = item?.timestamp || item?.timestamp_estimate;
        const quote = String(item?.quote || "").trim();
        // Description redundancy affects presentation only. A grounded timestamped
        // quote remains valid evidence even when the model repeats the finding.
        return timestamp
          && quote
          && quote.toLowerCase() !== "no direct quote";
      }).slice(0, 6)
    : [];
  if (!visible.length) return null;

  return (
    <div className="findings-key-moments findings-key-moments--evidence-first">
      {visible.map((item, index) => {
        const timestamp = item?.timestamp || item?.timestamp_estimate || `0${index + 1}`;
        const seconds = parseTimestampSeconds(timestamp);
        const timestampHref = Number.isFinite(seconds) && typeof timestampHrefBuilder === "function"
          ? timestampHrefBuilder(timestamp)
          : null;
        const quote = String(item?.quote || "").trim();
        const description = localizeSellerRole(String(item?.description || "").trim());
        const showDescription = !evidenceDescriptionIsRedundant(description, findingText, quote);

        return (
          <article key={`${timestamp}-${index}`} className="has-verbatim-quote">
            {timestampHref ? (
              <Link
                to={timestampHref}
                className="findings-key-moment__timestamp"
                title="Open this moment in the transcript"
                aria-label={`Open transcript at ${timestamp}`}
              >
                {timestamp}
              </Link>
            ) : (
              <span className="findings-key-moment__timestamp is-static">{timestamp}</span>
            )}
            <div className="findings-key-moment__evidence">
              <q className="findings-key-moment__quote evidence-verbatim-quote">{quote}</q>
              {showDescription && (
                <strong className="findings-key-moment__takeaway">{description}</strong>
              )}
            </div>
            <ConfChip confidence={item?.confidence} />
          </article>
        );
      })}
    </div>
  );
}

function EvidenceFindingCard({ item, timestampHrefBuilder, forceExpanded = false }) {
  const [expanded, setExpanded] = useState(forceExpanded);
  const finding = itemParts(item?.finding || item?.conclusion || item);
  const moments = Array.isArray(item?.supporting_moments)
    ? item.supporting_moments.filter((moment) => {
        const timestamp = moment?.timestamp || moment?.timestamp_estimate;
        const quote = String(moment?.quote || "").trim();
        return timestamp
          && quote
          && quote.toLowerCase() !== "no direct quote";
      }).slice(0, 3)
    : [];

  useEffect(() => {
    if (forceExpanded) setExpanded(true);
  }, [forceExpanded]);

  if (!finding.text || !moments.length) return null;

  return (
    <article className={`findings-evidence-card${expanded ? " is-expanded" : " is-collapsed"}`}>
      <button
        type="button"
        className="findings-evidence-card__toggle"
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
      >
        <span className="findings-evidence-card__finding">
          <strong>{localizeSellerRole(finding.text)}</strong>
        </span>
        <span className="findings-evidence-card__controls">
          <ConfChip confidence={finding.confidence} />
          <CaretDown size={16} weight="bold" aria-hidden="true" />
        </span>
      </button>

      {expanded && (
        <div className="findings-evidence-card__details">
          <div className="findings-evidence-card__moments">
            <small>Supporting moments</small>
            <KeyMomentList
              items={moments}
              timestampHrefBuilder={timestampHrefBuilder}
              findingText={finding.text}
            />
          </div>
        </div>
      )}
    </article>
  );
}

function EvidenceSection({ id, title, icon: Icon, items, timestampHrefBuilder, forceExpanded = false }) {
  const rawItems = Array.isArray(items) ? items : items ? [items] : [];
  const visibleItems = rawItems.filter((item) => {
    const finding = itemParts(item?.finding || item?.conclusion || item);
    const moments = Array.isArray(item?.supporting_moments) ? item.supporting_moments : [];
    return finding.text && moments.some((moment) => {
      const timestamp = moment?.timestamp || moment?.timestamp_estimate;
      const quote = String(moment?.quote || "").trim();
      return timestamp && quote && quote.toLowerCase() !== "no direct quote";
    });
  });
  if (!visibleItems.length) return null;

  return (
    <section id={`deep-dive-${id}`} className="findings-evidence-section">
      <div className="findings-evidence-section__header">
        <Icon size={13} weight="fill" aria-hidden="true" />
        <h3>{title}</h3>
      </div>
      <div className="findings-evidence-grid">
        {visibleItems.map((item, index) => (
          <EvidenceFindingCard
            key={`${id}-${index}`}
            item={item}
            timestampHrefBuilder={timestampHrefBuilder}
            forceExpanded={forceExpanded}
          />
        ))}
      </div>
    </section>
  );
}

function AnalysisView({
  analysis,
  onRun,
  loading,
  findingsStep,
  relationshipPath,
  relationshipName,
  relationshipReturnState,
  timestampHrefBuilder,
  deepDiveTarget,
}) {
  if (!analysis) {
    return (
      <section className="findings-empty-state">
        <div className="findings-empty-state__visual"><RevealLinesMark /></div>
        {loading ? (
          <div className="findings-processing findings-processing--centered">
            {["Scanning the conversation", "Connecting evidence"].map((label, index) => {
              const step = index + 1;
              const complete = findingsStep > step;
              const active = findingsStep === step;
              return (
                <div key={label} className={`findings-processing__step${active ? " is-active" : ""}${complete ? " is-complete" : ""}`}>
                  <span className="findings-processing__number">{complete ? "✓" : step}</span>
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            <div className="brand-eyebrow">Conversation dissection</div>
            <h2>Reveal what really happened.</h2>
            <p>Surface the commercial status, real friction, agreed actions and next routes forward.</p>
          </div>
        )}
        <button onClick={onRun} disabled={loading} className="call-detail-primary-button">
          <Target size={16} weight="fill" /> {loading ? "Building Dissection..." : "Start dissection"}
        </button>
      </section>
    );
  }

  const status = analysis.commercial_status || {};
  const deep = analysis.deep_dive || {};
  const takeaway = itemParts(analysis.takeaway || analysis.summary);
  const stage = status.stage || analysis.negotiation_stage;
  const decisionMaker = status.decision_maker || status.decision_owner || analysis.decision_maker_influence;
  const keyMoments = deep.key_moments || analysis.key_moments || [];
  const signalMetrics = sortScoreMetrics([
    { label: "Buying intent", value: analysis.buying_intent, reason: analysis.score_reasons?.buying_intent, description: SIGNAL_DESCRIPTIONS.buyingIntent },
    { label: "Trust", value: analysis.trust_level, reason: analysis.score_reasons?.trust_level, description: SIGNAL_DESCRIPTIONS.trust },
    { label: "Relationship", value: analysis.relationship_strength, reason: analysis.score_reasons?.relationship_strength, description: SIGNAL_DESCRIPTIONS.relationship },
    { label: "Confidence", value: analysis.confidence_level, reason: analysis.score_reasons?.confidence_level, description: SIGNAL_DESCRIPTIONS.confidence },
    { label: "Churn risk", value: analysis.risk_of_churn, reason: analysis.score_reasons?.risk_of_churn, description: SIGNAL_DESCRIPTIONS.churnRisk, directionNote: "More = higher risk", invert: true },
    { label: "Quality", value: analysis.conversation_quality, reason: analysis.score_reasons?.conversation_quality, description: SIGNAL_DESCRIPTIONS.quality },
  ]);
  const evidenceSections = [
    { id: "stance", title: "Stance", icon: Handshake, items: deep.stance },
    { id: "tone", title: "Tone", icon: ChatCircle, items: deep.tone?.signals || deep.tone },
    { id: "blockers", title: "Blockers", icon: WarningCircle, items: deep.blockers },
    { id: "objections", title: "Objections", icon: Flag, items: deep.objections },
    { id: "risks", title: "Risks", icon: Warning, items: deep.risks },
    { id: "opportunities", title: "Opportunities", icon: Lightbulb, items: deep.opportunities },
  ];
  const hasEvidenceSections = evidenceSections.some(({ items }) => {
    const candidates = Array.isArray(items) ? items : items ? [items] : [];
    return candidates.some((item) => (
      itemParts(item?.finding || item?.conclusion || item).text
      && Array.isArray(item?.supporting_moments)
      && item.supporting_moments.length
    ));
  });

  return (
    <div className="findings-view findings-view--classic" data-testid="analysis-view">
      <div className="findings-classic-grid">
        <section className="findings-signal-profile" style={{ animationDelay: "0ms" }}>
          <div className="findings-card__eyebrow">
            <ChartBar size={15} weight="fill" />
            Signals
          </div>
          <div className="findings-signal-profile__body">
            {signalMetrics.map((metric) => <Bar key={metric.label} {...metric} />)}
          </div>
        </section>

        <section className="findings-key-takeaway" style={{ animationDelay: "60ms" }}>
          <div className="findings-card__eyebrow">
            <MagnifyingGlass size={15} weight="fill" />
            Key takeaway
          </div>
          <h2 className="findings-key-takeaway__text">{localizeSellerRole(takeaway.text)}</h2>
          <div className="findings-takeaway-grid">
            <Kv label="Stage" val={stage} icon={TrendUp} />
            <Kv label="Decision maker" val={decisionMaker} icon={UserCircle} />
          </div>
        </section>
      </div>

      <section id="deep-dive" className="findings-deep-dive is-open">
        <div className="findings-deep-dive__header">
          <span>
            <Brain size={15} weight="fill" />
            DEEP DIVE
            <small>Why Callsect reached each conclusion</small>
          </span>
        </div>

        <div className="findings-deep-dive__content findings-deep-dive__content--evidence">
          {hasEvidenceSections ? (
            evidenceSections.map((section) => (
              <EvidenceSection
                key={section.id}
                {...section}
                timestampHrefBuilder={timestampHrefBuilder}
                forceExpanded={deepDiveTarget === section.id}
              />
            ))
          ) : (
            <div className="findings-deep-dive__legacy">
              <KeyMomentList items={keyMoments} timestampHrefBuilder={timestampHrefBuilder} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Kv({ label, val, color = "text-slate-200", icon: Icon }) {
  if (!val) return null;
  const { text, confidence } = itemParts(val);
  if (!text || text === "not evident") return null;
  return (
    <div className="findings-kv">
      <span className="findings-kv__label">
        {Icon && <Icon size={13} weight="fill" aria-hidden="true" />}
        {label}
      </span>
      <strong className={color}>{text}<ConfChip confidence={confidence} inline /></strong>
    </div>
  );
}

function CoachingView({ coaching, onRun, loading, timestampHrefBuilder, transcriptSegments }) {
  if (!coaching) {
    return (
      <section className="call-detail-empty-state call-detail-empty-state--large">
        <RevealLinesMark />
        <strong>Coaching is ready to reveal.</strong>
        <p>Open the coaching layer to turn the Dissection into practical next moves.</p>
        <button onClick={onRun} disabled={loading} className="call-detail-primary-button">
          <ChartBar size={16} weight="fill" /> {loading ? "Preparing coaching..." : "Reveal coaching"}
        </button>
      </section>
    );
  }

  const skillMetrics = sortScoreMetrics([
    { label: "Empathy", value: coaching.empathy_score, reason: coaching.skill_reasons?.empathy_score, description: SKILL_DESCRIPTIONS.empathy },
    { label: "Listening", value: coaching.listening_score, reason: coaching.skill_reasons?.listening_score, description: SKILL_DESCRIPTIONS.listening },
    { label: "Conversation control", value: coaching.control_of_conversation, reason: coaching.skill_reasons?.control_of_conversation, description: SKILL_DESCRIPTIONS.conversationControl },
  ]);
  const speakerResolver = (timestamp) => coachingSpeakerAtTimestamp(transcriptSegments, timestamp);

  return (
    <div className="coaching-view coaching-view--structured" data-testid="coaching-view">
      <div className="coaching-overview-grid">
        <section className="coaching-card coaching-card--skills" style={{ animationDelay: "0ms" }}>
          <div className="findings-card__eyebrow"><ChartBar size={15} weight="fill" /> Skills</div>
          <div className="coaching-card__body">
            {skillMetrics.map((metric) => <Bar key={metric.label} {...metric} />)}
          </div>
        </section>

        <section className="coaching-card" style={{ animationDelay: "70ms" }}>
          <div className="findings-card__eyebrow"><CheckCircle size={15} weight="fill" /> What worked</div>
          <div className="coaching-card__body"><List title="Strengths to retain" items={coaching.what_worked} icon={Barbell} color="text-emerald-300" italicQuotes showTimestamps timestampHrefBuilder={timestampHrefBuilder} speakerResolver={speakerResolver} /></div>
        </section>
      </div>

      <section className="coaching-card coaching-card--moves" style={{ animationDelay: "140ms" }}>
        <div className="findings-card__eyebrow"><Megaphone size={15} weight="fill" /> Coaching moves</div>
        <div className="coaching-moves">
          {coaching.coaching_moves?.map((move, index) => (
            <article key={index} className="coaching-move">
              <div className="coaching-move__header"><strong>{localizeSellerRole(move.title)}</strong><ConfChip confidence={move.confidence} /></div>
              <div className="coaching-move__section">
                <span>Situation</span>
                <div className="coaching-move__evidence">
                  <span className="findings-list__bullet coaching-move__bullet" aria-hidden="true" />
                  <span className="coaching-evidence__line coaching-evidence__line--with-meta coaching-move__evidence-line">
                    <CoachingEvidenceMeta
                      speaker={speakerResolver(move.timestamp)}
                      timestamp={move.timestamp}
                      href={typeof timestampHrefBuilder === "function" ? timestampHrefBuilder(move.timestamp) : null}
                    />
                    {(() => {
                      const situation = localizeSellerRole(move.situation);
                      const { quote, commentary } = splitLeadingEvidenceQuote(situation);
                      return (
                        <span className="coaching-move__situation-copy">
                          <blockquote className="evidence-verbatim-quote coaching-move__situation">
                            {quote || `“${situation}”`}
                          </blockquote>
                          {quote && commentary && <p className="coaching-move__situation-commentary">{commentary}</p>}
                        </span>
                      );
                    })()}
                  </span>
                </div>
              </div>
              <div className="coaching-move__section coaching-move__section--continuation"><span>Better approach</span><p>{renderWithItalicQuotes(localizeSellerRole(move.better_move))}</p></div>
              <div className="coaching-move__section coaching-move__section--continuation"><span>Example wording</span><blockquote className="is-example">“{localizeSellerRole(move.example_phrase)}”</blockquote></div>
              <div className="coaching-move__section coaching-move__section--continuation"><span>Why it works</span><p>{renderWithItalicQuotes(localizeSellerRole(move.why_it_works))}</p></div>
            </article>
          ))}
        </div>
      </section>

      <section className="coaching-card coaching-card--wide" style={{ animationDelay: "210ms" }}>
        <div className="findings-card__eyebrow"><Target size={15} weight="fill" /> Next conversation</div>
        <div className="coaching-card__columns">
          <List title="Missed opportunities" items={coaching.missed_opportunities} icon={ArrowCircleUpRight} italicQuotes showTimestamps timestampHrefBuilder={timestampHrefBuilder} speakerResolver={speakerResolver} />
          <List title="Questions to ask" items={coaching.questions_to_ask} icon={ChatsCircle} questionPrompt showTimestamps timestampHrefBuilder={timestampHrefBuilder} speakerResolver={speakerResolver} />
          <List title="Negotiation suggestions" items={coaching.negotiation_suggestions} icon={Handshake} italicQuotes />
        </div>
      </section>
    </div>
  );
}
